// src/standard/StandardWidget.tsx
import { useState } from "react";
import { ChatProvider } from "@dopomogai/chat-react";
import { ChatUIProvider, NeobrutalismTheme, MessageList } from "@dopomogai/chat-ui";
import { MessageSquare, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { jsx, jsxs } from "react/jsx-runtime";
var StandardWidget = ({ agentId, apiBaseUrl }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { Composer } = NeobrutalismTheme;
  return /* @__PURE__ */ jsx(ChatProvider, { agentId, apiBaseUrl, children: /* @__PURE__ */ jsx(ChatUIProvider, { theme: NeobrutalismTheme, children: /* @__PURE__ */ jsxs("div", { className: "fixed bottom-6 right-6 z-[9999] font-sans", children: [
    /* @__PURE__ */ jsx(AnimatePresence, { children: isOpen && /* @__PURE__ */ jsxs(
      motion.div,
      {
        initial: { opacity: 0, y: 20, scale: 0.95 },
        animate: { opacity: 1, y: 0, scale: 1 },
        exit: { opacity: 0, y: 20, scale: 0.95 },
        className: "w-[380px] h-[600px] max-h-[80vh] max-w-[calc(100vw-40px)] bg-white border-2 border-black rounded-xl shadow-[8px_8px_0_0_#000] flex flex-col overflow-hidden",
        children: [
          /* @__PURE__ */ jsxs("div", { className: "p-4 border-b-2 border-black bg-emerald-100 flex justify-between items-center", children: [
            /* @__PURE__ */ jsx("h3", { className: "font-bold text-lg text-black", children: "Assistant" }),
            /* @__PURE__ */ jsx("button", { onClick: () => setIsOpen(false), className: "p-1 hover:bg-black/10 rounded text-black", children: /* @__PURE__ */ jsx(X, { size: 20 }) })
          ] }),
          /* @__PURE__ */ jsx("div", { className: "flex-1 overflow-hidden relative bg-slate-50", children: /* @__PURE__ */ jsx(MessageList, { className: "h-full" }) }),
          /* @__PURE__ */ jsx(Composer, { onSend: () => {
          }, isLoading: false })
        ]
      }
    ) }),
    !isOpen && /* @__PURE__ */ jsx(
      "button",
      {
        onClick: () => setIsOpen(true),
        className: "w-14 h-14 bg-emerald-500 border-2 border-black rounded-full flex items-center justify-center shadow-[4px_4px_0_0_#000] hover:-translate-y-1 hover:shadow-[6px_6px_0_0_#000] transition-all active:translate-y-0 active:shadow-none",
        children: /* @__PURE__ */ jsx(MessageSquare, { color: "white" })
      }
    )
  ] }) }) });
};

// src/audio/AudioWidget.tsx
import { ChatProvider as ChatProvider2 } from "@dopomogai/chat-react";
import { Mic } from "lucide-react";
import { jsx as jsx2 } from "react/jsx-runtime";
var AudioOrb = () => {
  return /* @__PURE__ */ jsx2("div", { className: "fixed bottom-6 right-6 z-[9999]", children: /* @__PURE__ */ jsx2("button", { className: "w-16 h-16 rounded-full bg-black border-4 border-white shadow-xl flex items-center justify-center text-white hover:scale-110 transition-transform", children: /* @__PURE__ */ jsx2(Mic, {}) }) });
};
var AudioWidget = ({ agentId, apiBaseUrl }) => {
  return /* @__PURE__ */ jsx2(ChatProvider2, { agentId, apiBaseUrl, children: /* @__PURE__ */ jsx2(AudioOrb, {}) });
};

// src/studio/StudioWidget.tsx
import { useCallback as useCallback2, useEffect as useEffect2, useMemo as useMemo8, useRef as useRef2, useState as useState8 } from "react";
import { useStore as useStore2 } from "zustand";
import { useShallow as useShallow2 } from "zustand/react/shallow";
import clsx13 from "clsx";
import "@dopomogai/chat-ui/styles/chat-markdown.css";
import {
  modelService,
  contextAnalysisService,
  agentService,
  conversationUsageService,
  isApiError
} from "@dopomogai/api-client";
import { ChatProvider as ChatProvider3, useChatClient as useChatClient2, useChat } from "@dopomogai/chat-react";
import { ChatUIProvider as ChatUIProvider2, MessageList as MessageList2, GlassTheme } from "@dopomogai/chat-ui";

// src/studio/components/StudioLayout.tsx
import clsx from "clsx";
import { Menu } from "lucide-react";
import { jsx as jsx3, jsxs as jsxs2 } from "react/jsx-runtime";
var StudioLayout = ({
  sidebar,
  header,
  tabs,
  messages,
  composer,
  isSidebarOpenMobile,
  onSetSidebarOpenMobile,
  isSidebarCollapsed,
  composerPlacement = "bottom",
  hero
}) => {
  const sidebarWidth = isSidebarCollapsed ? "4rem" : "18rem";
  return /* @__PURE__ */ jsxs2("div", { className: "flex flex-row h-full w-full overflow-hidden bg-slate-50 text-slate-900 font-sans relative select-text", children: [
    sidebar && isSidebarOpenMobile && /* @__PURE__ */ jsx3(
      "div",
      {
        className: "fixed inset-0 bg-black/40 z-40 md:hidden backdrop-blur-sm animate-in fade-in",
        onClick: () => onSetSidebarOpenMobile(false)
      }
    ),
    sidebar ? /* @__PURE__ */ jsx3(
      "div",
      {
        className: clsx(
          "h-full bg-white border-r border-slate-200 transition-all duration-300 ease-in-out z-50 flex-shrink-0",
          "fixed inset-y-0 left-0 md:static",
          isSidebarOpenMobile ? "translate-x-0" : "-translate-x-full md:translate-x-0",
          isSidebarCollapsed ? "w-16" : "w-72"
        ),
        children: sidebar
      }
    ) : null,
    /* @__PURE__ */ jsxs2(
      "div",
      {
        className: clsx(
          "flex-1 flex flex-col h-full min-w-0 relative z-0 bg-slate-50/50 transition-[margin] duration-300",
          sidebar && isSidebarOpenMobile ? "ml-72 md:ml-0" : "ml-0"
        ),
        style: { marginLeft: sidebar && isSidebarOpenMobile ? sidebarWidth : void 0 },
        children: [
          /* @__PURE__ */ jsxs2("div", { className: "shrink-0 z-50 bg-white/80 backdrop-blur-sm relative border-b border-slate-200/50 select-none", children: [
            sidebar ? /* @__PURE__ */ jsxs2("div", { className: "md:hidden flex items-center p-3 border-b border-slate-200 gap-3", children: [
              /* @__PURE__ */ jsx3(
                "button",
                {
                  onClick: () => onSetSidebarOpenMobile(true),
                  className: "p-1 hover:bg-slate-100 rounded",
                  "aria-label": "Open sidebar",
                  children: /* @__PURE__ */ jsx3(Menu, { size: 20 })
                }
              ),
              /* @__PURE__ */ jsx3("div", { className: "flex-1 font-semibold text-sm", children: "Studio" })
            ] }) : null,
            tabs || null,
            /* @__PURE__ */ jsx3("div", { className: "hidden md:block", children: header })
          ] }),
          /* @__PURE__ */ jsxs2("div", { className: "flex-1 relative w-full h-full min-h-0 overflow-hidden", children: [
            /* @__PURE__ */ jsx3("div", { className: "absolute inset-0 overflow-y-auto overflow-x-hidden custom-scrollbar scroll-smooth", children: /* @__PURE__ */ jsx3("div", { className: "w-full max-w-5xl mx-auto px-4 md:px-8 pt-6 pb-48 flex flex-col min-h-full", children: /* @__PURE__ */ jsx3("div", { className: "flex-1", children: composerPlacement === "center" ? /* @__PURE__ */ jsxs2("div", { className: "min-h-[calc(100vh-240px)] flex flex-col items-center justify-center gap-8", children: [
              hero,
              /* @__PURE__ */ jsx3("div", { className: "w-full max-w-3xl", children: composer })
            ] }) : messages }) }) }),
            composerPlacement === "bottom" && /* @__PURE__ */ jsx3("div", { className: "absolute bottom-0 left-0 right-0 z-10 pointer-events-none pb-8 px-4", children: /* @__PURE__ */ jsx3("div", { className: "w-full max-w-3xl mx-auto pointer-events-auto", children: composer }) })
          ] })
        ]
      }
    )
  ] });
};

// src/studio/components/StudioSidebar.tsx
import { useState as useState2 } from "react";
import clsx2 from "clsx";
import {
  Plus,
  MessageSquare as MessageSquare2,
  Search,
  Trash2,
  Edit2,
  PanelLeftClose,
  PanelLeftOpen,
  Check,
  X as X2
} from "lucide-react";
import { jsx as jsx4, jsxs as jsxs3 } from "react/jsx-runtime";
var StudioSidebar = ({
  conversations,
  activeConversationId,
  onSelectConversation,
  onDeleteConversation,
  onRenameConversation,
  isCollapsed,
  onToggleCollapse,
  isLoading,
  className
}) => {
  const [searchTerm, setSearchTerm] = useState2("");
  const [editingId, setEditingId] = useState2(null);
  const [editValue, setEditValue] = useState2("");
  const filtered = conversations.filter(
    (c) => (c.first_user_message_preview || c.title || "New Chat").toLowerCase().includes(searchTerm.toLowerCase())
  );
  const getGroupLabel = (dateStr) => {
    const date = new Date(dateStr);
    const now = /* @__PURE__ */ new Date();
    if (date.toDateString() === now.toDateString()) return "Today";
    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
    return "Older";
  };
  const grouped = filtered.reduce((acc, conv) => {
    const label = getGroupLabel(conv.updated_at);
    if (!acc[label]) acc[label] = [];
    acc[label].push(conv);
    return acc;
  }, {});
  const sections = ["Today", "Yesterday", "Older"];
  const handleStartEdit = (e, id, currentTitle) => {
    e.stopPropagation();
    setEditingId(id);
    setEditValue(currentTitle);
  };
  const handleSaveEdit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (editingId && editValue.trim()) {
      onRenameConversation(editingId, editValue.trim());
    }
    setEditingId(null);
  };
  const handleCancelEdit = (e) => {
    e.stopPropagation();
    setEditingId(null);
  };
  if (isCollapsed) {
    return /* @__PURE__ */ jsxs3(
      "div",
      {
        className: clsx2(
          "flex flex-col h-full bg-slate-50 border-r border-slate-200 w-16 items-center py-3 gap-4 flex-shrink-0 transition-all z-30",
          className
        ),
        children: [
          /* @__PURE__ */ jsx4(
            "button",
            {
              onClick: onToggleCollapse,
              className: "p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-200 rounded-lg transition-colors",
              title: "Expand Sidebar",
              children: /* @__PURE__ */ jsx4(PanelLeftOpen, { size: 20 })
            }
          ),
          /* @__PURE__ */ jsx4(
            "button",
            {
              onClick: () => onSelectConversation(null),
              className: "w-10 h-10 bg-blue-600 text-white rounded-xl shadow-md hover:bg-blue-700 hover:shadow-lg transition-all flex items-center justify-center active:scale-95",
              title: "New Chat",
              children: /* @__PURE__ */ jsx4(Plus, { size: 22 })
            }
          ),
          /* @__PURE__ */ jsx4("div", { className: "w-8 h-px bg-slate-200" }),
          /* @__PURE__ */ jsx4("div", { className: "flex-1 w-full flex flex-col items-center gap-3 overflow-y-auto overflow-x-hidden custom-scrollbar pb-2 min-h-0", children: conversations.slice(0, 15).map((c) => /* @__PURE__ */ jsx4(
            "button",
            {
              onClick: () => onSelectConversation(c.id),
              className: clsx2(
                "w-10 h-10 rounded-lg flex items-center justify-center transition-all flex-shrink-0",
                c.id === activeConversationId ? "bg-white border-2 border-blue-500 text-blue-600 shadow-sm" : "text-slate-400 hover:bg-slate-200 hover:text-slate-600"
              ),
              title: c.title || c.first_user_message_preview || "Chat",
              children: /* @__PURE__ */ jsx4(MessageSquare2, { size: 20 })
            },
            c.id
          )) })
        ]
      }
    );
  }
  return /* @__PURE__ */ jsxs3(
    "div",
    {
      className: clsx2(
        "flex flex-col h-full bg-slate-50 border-r border-slate-200 w-72 flex-shrink-0 transition-all z-30",
        className
      ),
      children: [
        /* @__PURE__ */ jsxs3("div", { className: "flex items-center justify-between px-4 py-3 border-b border-slate-200 flex-shrink-0 h-14 bg-slate-50", children: [
          /* @__PURE__ */ jsxs3("div", { className: "flex items-center gap-2 text-slate-700 font-bold text-xs tracking-wider uppercase", children: [
            /* @__PURE__ */ jsx4("span", { children: "Chats" }),
            /* @__PURE__ */ jsx4("span", { className: "bg-slate-200 text-slate-600 px-1.5 py-0.5 rounded-md", children: conversations.length })
          ] }),
          /* @__PURE__ */ jsx4(
            "button",
            {
              onClick: onToggleCollapse,
              className: "text-slate-400 hover:text-slate-600 p-1.5 rounded-md hover:bg-slate-200 transition-colors",
              children: /* @__PURE__ */ jsx4(PanelLeftClose, { size: 18 })
            }
          )
        ] }),
        /* @__PURE__ */ jsxs3("div", { className: "p-3 space-y-3 flex-shrink-0", children: [
          /* @__PURE__ */ jsxs3(
            "button",
            {
              onClick: () => onSelectConversation(null),
              className: clsx2(
                "flex items-center justify-center gap-2 w-full py-2.5 rounded-lg border text-sm font-semibold transition-all active:scale-[0.98]",
                !activeConversationId ? "bg-slate-200 border-slate-300 text-slate-800 shadow-inner" : "bg-white border-slate-200 text-slate-600 hover:border-blue-400 hover:text-blue-600 shadow-sm"
              ),
              children: [
                /* @__PURE__ */ jsx4(Plus, { size: 16 }),
                /* @__PURE__ */ jsx4("span", { children: "New Chat" })
              ]
            }
          ),
          /* @__PURE__ */ jsxs3("div", { className: "relative group", children: [
            /* @__PURE__ */ jsx4(
              Search,
              {
                size: 14,
                className: "absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-blue-500 transition-colors"
              }
            ),
            /* @__PURE__ */ jsx4(
              "input",
              {
                type: "text",
                placeholder: "Search conversations...",
                value: searchTerm,
                onChange: (e) => setSearchTerm(e.target.value),
                className: "w-full bg-white border border-slate-200 rounded-lg pl-9 pr-3 py-2 text-xs font-medium focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400 transition-all"
              }
            )
          ] })
        ] }),
        /* @__PURE__ */ jsxs3("div", { className: "flex-1 overflow-y-auto px-2 pb-2 custom-scrollbar min-h-0", children: [
          isLoading && conversations.length === 0 && /* @__PURE__ */ jsxs3("div", { className: "py-8 text-center text-xs text-slate-400 flex flex-col items-center gap-2", children: [
            /* @__PURE__ */ jsx4("div", { className: "w-4 h-4 border-2 border-slate-300 border-t-transparent rounded-full animate-spin" }),
            "Loading history..."
          ] }),
          !isLoading && filtered.length === 0 && conversations.length > 0 && /* @__PURE__ */ jsx4("div", { className: "py-8 text-center text-xs text-slate-400", children: "No chats found." }),
          sections.map((section) => {
            const items = grouped[section];
            if (!items || items.length === 0) return null;
            return /* @__PURE__ */ jsxs3("div", { className: "mb-2", children: [
              /* @__PURE__ */ jsx4("div", { className: "px-3 py-2 text-[10px] font-extrabold text-slate-400 uppercase tracking-widest sticky top-0 bg-slate-50/95 backdrop-blur-sm z-10 border-b border-transparent", children: section }),
              /* @__PURE__ */ jsx4("div", { className: "space-y-1", children: items.map((conv) => {
                const isActive = conv.id === activeConversationId;
                const isEditing = editingId === conv.id;
                const displayTitle = conv.title || conv.first_user_message_preview || "New Chat";
                return /* @__PURE__ */ jsxs3(
                  "div",
                  {
                    className: clsx2(
                      "group relative flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-all text-sm min-h-[40px]",
                      isActive ? "bg-white text-blue-700 shadow-sm border border-slate-200 ring-1 ring-black/5" : "text-slate-600 hover:bg-slate-200/50 hover:text-slate-900 border border-transparent"
                    ),
                    onClick: () => !isEditing && onSelectConversation(conv.id),
                    children: [
                      /* @__PURE__ */ jsx4(
                        MessageSquare2,
                        {
                          size: 16,
                          className: clsx2(
                            "shrink-0 transition-colors",
                            isActive ? "text-blue-600" : "text-slate-400 group-hover:text-slate-500"
                          )
                        }
                      ),
                      isEditing ? /* @__PURE__ */ jsxs3(
                        "form",
                        {
                          onSubmit: handleSaveEdit,
                          className: "flex-1 flex items-center gap-1 min-w-0 animate-in fade-in duration-200",
                          onClick: (e) => e.stopPropagation(),
                          children: [
                            /* @__PURE__ */ jsx4(
                              "input",
                              {
                                autoFocus: true,
                                className: "w-full text-xs bg-white border border-blue-400 rounded px-1.5 py-1 outline-none shadow-sm text-slate-800",
                                value: editValue,
                                onChange: (e) => setEditValue(e.target.value),
                                onKeyDown: (e) => {
                                  if (e.key === "Escape") handleCancelEdit(e);
                                }
                              }
                            ),
                            /* @__PURE__ */ jsx4("button", { type: "submit", className: "text-emerald-600 hover:bg-emerald-100 p-1 rounded-md transition-colors", children: /* @__PURE__ */ jsx4(Check, { size: 14 }) }),
                            /* @__PURE__ */ jsx4(
                              "button",
                              {
                                type: "button",
                                onClick: (e) => handleCancelEdit(e),
                                className: "text-red-500 hover:bg-red-100 p-1 rounded-md transition-colors",
                                children: /* @__PURE__ */ jsx4(X2, { size: 14 })
                              }
                            )
                          ]
                        }
                      ) : /* @__PURE__ */ jsx4("div", { className: "flex-1 min-w-0 flex flex-col justify-center", children: /* @__PURE__ */ jsx4("span", { className: clsx2("truncate font-medium leading-snug", isActive ? "text-blue-700" : "text-slate-700"), children: displayTitle }) }),
                      !isEditing && /* @__PURE__ */ jsxs3(
                        "div",
                        {
                          className: clsx2(
                            "absolute right-2 flex items-center gap-1 bg-gradient-to-l from-white via-white to-transparent pl-4 transition-opacity",
                            isActive ? "opacity-100" : "opacity-0 group-hover:opacity-100"
                          ),
                          children: [
                            /* @__PURE__ */ jsx4(
                              "button",
                              {
                                onClick: (e) => handleStartEdit(e, conv.id, displayTitle),
                                className: "p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors",
                                title: "Rename",
                                children: /* @__PURE__ */ jsx4(Edit2, { size: 13 })
                              }
                            ),
                            /* @__PURE__ */ jsx4(
                              "button",
                              {
                                onClick: (e) => {
                                  e.stopPropagation();
                                  onDeleteConversation(conv.id);
                                },
                                className: "p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors",
                                title: "Delete",
                                children: /* @__PURE__ */ jsx4(Trash2, { size: 13 })
                              }
                            )
                          ]
                        }
                      )
                    ]
                  },
                  conv.id
                );
              }) })
            ] }, section);
          })
        ] })
      ]
    }
  );
};

// src/studio/components/StudioHeader.tsx
import { useMemo as useMemo2, useState as useState4 } from "react";
import clsx4 from "clsx";
import { Bot, Settings2, ChevronDown, Share2, Cpu, Check as Check2, Loader2, SlidersHorizontal as SlidersHorizontal2, DollarSign, Clock, MessageSquare as MessageSquare3 } from "lucide-react";
import { useChatClient } from "@dopomogai/chat-react";
import { useStore } from "zustand";
import { useShallow } from "zustand/react/shallow";

// src/studio/components/ModelSettingsPopover.tsx
import React3, { useMemo, useState as useState3 } from "react";
import clsx3 from "clsx";
import { X as X3, SlidersHorizontal } from "lucide-react";
import { jsx as jsx5, jsxs as jsxs4 } from "react/jsx-runtime";
function ModelSettingsPopover({
  open,
  onClose,
  providerHint,
  value,
  onChange,
  onSaveToAgent
  // optional hook; can be no-op now
}) {
  const [local, setLocal] = useState3(value || {});
  React3.useEffect(() => {
    if (open) setLocal(value || {});
  }, [open]);
  const isAnthropic = useMemo(() => providerHint.toLowerCase().includes("anthropic"), [providerHint]);
  const isOpenAI = useMemo(() => providerHint.toLowerCase().includes("openai") || providerHint.toLowerCase().includes("azure"), [providerHint]);
  if (!open) return null;
  const set = (patch) => setLocal((p) => ({ ...p, ...patch }));
  const thinking = local.thinking?.type === "enabled";
  return /* @__PURE__ */ jsxs4("div", { className: "fixed inset-0 z-50", children: [
    /* @__PURE__ */ jsx5("button", { className: "absolute inset-0 bg-black/30", onClick: onClose, "aria-label": "Close overlay" }),
    /* @__PURE__ */ jsxs4("div", { className: "absolute top-16 right-4 w-[420px] max-w-[calc(100vw-2rem)] bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden", children: [
      /* @__PURE__ */ jsxs4("div", { className: "px-4 py-3 border-b border-slate-200 flex items-center justify-between", children: [
        /* @__PURE__ */ jsxs4("div", { className: "flex items-center gap-2", children: [
          /* @__PURE__ */ jsx5(SlidersHorizontal, { size: 16, className: "text-slate-500" }),
          /* @__PURE__ */ jsxs4("div", { children: [
            /* @__PURE__ */ jsx5("div", { className: "text-sm font-bold text-slate-900", children: "Model settings (override)" }),
            /* @__PURE__ */ jsx5("div", { className: "text-xs text-slate-500", children: "Applied only to this conversation." })
          ] })
        ] }),
        /* @__PURE__ */ jsx5("button", { onClick: onClose, className: "p-2 rounded hover:bg-slate-100", "aria-label": "Close", children: /* @__PURE__ */ jsx5(X3, { size: 16 }) })
      ] }),
      /* @__PURE__ */ jsxs4("div", { className: "p-4 space-y-4", children: [
        /* @__PURE__ */ jsxs4("div", { children: [
          /* @__PURE__ */ jsxs4("div", { className: "flex items-center justify-between mb-1", children: [
            /* @__PURE__ */ jsx5("label", { className: "text-xs font-semibold text-slate-700", children: "Temperature" }),
            /* @__PURE__ */ jsx5("span", { className: "text-xs font-mono text-slate-500", children: local.temperature ?? 0.7 })
          ] }),
          /* @__PURE__ */ jsx5(
            "input",
            {
              type: "range",
              min: 0,
              max: 2,
              step: 0.1,
              value: local.temperature ?? 0.7,
              onChange: (e) => set({ temperature: Number(e.target.value) }),
              className: "w-full"
            }
          )
        ] }),
        /* @__PURE__ */ jsxs4("div", { children: [
          /* @__PURE__ */ jsx5("label", { className: "text-xs font-semibold text-slate-700", children: "Max output tokens" }),
          /* @__PURE__ */ jsx5(
            "input",
            {
              type: "number",
              value: local.max_tokens ?? "",
              placeholder: "e.g. 4096",
              onChange: (e) => set({ max_tokens: e.target.value ? Number(e.target.value) : void 0 }),
              className: "mt-1 w-full h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-200"
            }
          )
        ] }),
        /* @__PURE__ */ jsxs4("div", { className: "border-t border-slate-100 pt-4", children: [
          /* @__PURE__ */ jsxs4("div", { className: "flex items-center justify-between", children: [
            /* @__PURE__ */ jsxs4("div", { children: [
              /* @__PURE__ */ jsx5("div", { className: "text-xs font-semibold text-slate-700", children: "Reasoning / Thinking" }),
              /* @__PURE__ */ jsx5("div", { className: "text-[11px] text-slate-500", children: "Provider-specific behavior." })
            ] }),
            /* @__PURE__ */ jsx5(
              "button",
              {
                onClick: () => setLocal((p) => ({
                  ...p,
                  thinking: p.thinking?.type === "enabled" ? { type: "disabled" } : { type: "enabled" }
                })),
                className: clsx3(
                  "px-2 py-1 rounded text-xs font-bold border",
                  thinking ? "bg-indigo-50 text-indigo-700 border-indigo-200" : "bg-white text-slate-600 border-slate-200"
                ),
                children: thinking ? "Enabled" : "Disabled"
              }
            )
          ] }),
          thinking && isAnthropic && /* @__PURE__ */ jsxs4("div", { className: "mt-3", children: [
            /* @__PURE__ */ jsx5("label", { className: "text-xs font-semibold text-slate-700", children: "Thinking budget (tokens)" }),
            /* @__PURE__ */ jsx5(
              "input",
              {
                type: "number",
                value: local.thinking?.budget_tokens ?? 4096,
                onChange: (e) => setLocal((p) => ({
                  ...p,
                  thinking: { type: "enabled", budget_tokens: Number(e.target.value || 0) || 0 }
                })),
                className: "mt-1 w-full h-9 px-3 text-sm border border-slate-200 rounded-md"
              }
            )
          ] }),
          thinking && isOpenAI && /* @__PURE__ */ jsxs4("div", { className: "mt-3", children: [
            /* @__PURE__ */ jsx5("label", { className: "text-xs font-semibold text-slate-700", children: "Reasoning effort" }),
            /* @__PURE__ */ jsx5("div", { className: "mt-2 flex gap-2", children: ["low", "medium", "high"].map((lvl) => {
              const current = local.thinking?.effort || "medium";
              const active = current === lvl;
              return /* @__PURE__ */ jsx5(
                "button",
                {
                  onClick: () => setLocal((p) => ({ ...p, thinking: { type: "enabled", effort: lvl } })),
                  className: clsx3(
                    "px-3 py-1 rounded text-xs font-bold border capitalize",
                    active ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-600 border-slate-200"
                  ),
                  children: lvl
                },
                lvl
              );
            }) })
          ] })
        ] }),
        /* @__PURE__ */ jsxs4("div", { className: "flex items-center justify-between pt-2", children: [
          /* @__PURE__ */ jsx5(
            "button",
            {
              onClick: () => {
                onChange(local);
                onClose();
              },
              className: "px-3 py-2 rounded-md bg-blue-600 text-white text-xs font-bold hover:bg-blue-700",
              children: "Apply override"
            }
          ),
          /* @__PURE__ */ jsx5(
            "button",
            {
              onClick: () => onSaveToAgent?.(),
              className: "text-xs font-semibold text-slate-500 hover:text-slate-800",
              title: "Optional: save these values into the agent configuration",
              children: "Save to agent\u2026"
            }
          )
        ] })
      ] })
    ] })
  ] });
}

// src/studio/components/StudioHeader.tsx
import { jsx as jsx6, jsxs as jsxs5 } from "react/jsx-runtime";
var headerSelector = (s) => {
  const activeId = s.activeConversationId || null;
  return { conversationId: activeId };
};
function formatModelLabel(m) {
  return `${m.name} \u2022 ${m.provider}`;
}
function formatCompactInt(n) {
  return n.toLocaleString();
}
function fmtUsd(n) {
  if (!Number.isFinite(n)) return "$0.0000";
  return `$${n.toFixed(4)}`;
}
var StudioHeader = ({
  agents,
  activeAgentId,
  onAgentChange,
  models,
  activeModelId,
  onModelChange,
  effectiveModel,
  modelSettings,
  onModelSettingsChange,
  onOpenAgentPanel,
  contextUsage,
  onOpenContextDetails,
  costUsd,
  onOpenCostDetails,
  isCostLoading,
  isSettingsOpen,
  onToggleSettings,
  onOpenHistory,
  injectCommentsEnabled = false,
  onToggleInjectComments,
  className
}) => {
  const client = useChatClient();
  const { conversationId } = useStore(client.store, useShallow(headerSelector));
  const [isSharing, setIsSharing] = useState4(false);
  const [shareSuccess, setShareSuccess] = useState4(false);
  const [isModelSettingsOpen, setIsModelSettingsOpen] = useState4(false);
  const activeAgent = useMemo2(() => agents.find((a) => a.id === activeAgentId) || null, [agents, activeAgentId]);
  const contextText = useMemo2(() => {
    if (!contextUsage) return null;
    const used = formatCompactInt(contextUsage.total_tokens || 0);
    const limit = formatCompactInt(contextUsage.model_context_limit || 0);
    return `${used} / ${limit} t`;
  }, [contextUsage]);
  const contextTone = useMemo2(() => {
    if (!contextUsage?.model_context_limit) return "text-slate-500";
    const used = contextUsage.total_tokens || 0;
    const limit = contextUsage.model_context_limit || 1;
    const ratio = used / limit;
    if (contextUsage.is_limit_exceeded || ratio >= 1) return "text-rose-600";
    if (ratio >= 0.9) return "text-orange-600";
    if (ratio >= 0.7) return "text-amber-600";
    return "text-slate-500";
  }, [contextUsage]);
  const modelTag = useMemo2(() => {
    return activeModelId ? { text: "Override", cls: "bg-purple-50 text-purple-700 border-purple-200" } : { text: "Default", cls: "bg-slate-50 text-slate-600 border-slate-200" };
  }, [activeModelId]);
  const settingsTag = useMemo2(() => {
    const hasSettings = modelSettings && Object.keys(modelSettings).length > 0;
    return hasSettings ? { text: "Override", cls: "bg-indigo-50 text-indigo-700 border-indigo-200" } : { text: "Default", cls: "bg-slate-50 text-slate-600 border-slate-200" };
  }, [modelSettings]);
  const handleShare = async () => {
    if (!conversationId) return;
    setIsSharing(true);
    try {
      const url = await client.shareConversation(conversationId);
      await navigator.clipboard.writeText(url);
      setShareSuccess(true);
      window.setTimeout(() => setShareSuccess(false), 2e3);
    } catch (e) {
      console.error("[StudioHeader] Share failed", e);
      alert("Failed to create share link");
    } finally {
      setIsSharing(false);
    }
  };
  return /* @__PURE__ */ jsxs5(
    "header",
    {
      className: clsx4(
        "h-11 bg-white border-b border-slate-200 flex items-center justify-between px-4 shadow-sm z-20 flex-shrink-0 select-none",
        className
      ),
      children: [
        /* @__PURE__ */ jsxs5("div", { className: "flex items-center gap-4 min-w-0", children: [
          /* @__PURE__ */ jsxs5("div", { className: "relative group min-w-0", children: [
            /* @__PURE__ */ jsxs5("div", { className: "flex items-center gap-3 cursor-pointer p-1 rounded-lg hover:bg-slate-50 transition-colors min-w-0", children: [
              /* @__PURE__ */ jsx6("div", { className: "w-9 h-9 bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg flex items-center justify-center text-white shadow-sm shadow-blue-200 group-hover:shadow-md transition-all", children: /* @__PURE__ */ jsx6(Bot, { size: 18 }) }),
              /* @__PURE__ */ jsxs5("div", { className: "flex flex-col min-w-0", children: [
                /* @__PURE__ */ jsx6("span", { className: "text-[10px] font-bold text-slate-400 uppercase tracking-wider leading-none mb-1", children: "Agent" }),
                /* @__PURE__ */ jsxs5("div", { className: "flex items-center gap-1 font-bold text-slate-800 text-sm leading-none group-hover:text-blue-600 transition-colors min-w-0", children: [
                  /* @__PURE__ */ jsx6("span", { className: "truncate max-w-[140px] md:max-w-[220px]", children: activeAgent?.name || "Select Agent" }),
                  /* @__PURE__ */ jsx6(ChevronDown, { size: 12, className: "text-slate-400 shrink-0" })
                ] })
              ] })
            ] }),
            /* @__PURE__ */ jsx6(
              "select",
              {
                className: "absolute inset-0 w-full h-full opacity-0 cursor-pointer",
                value: activeAgentId || "",
                onChange: (e) => onAgentChange(e.target.value),
                "aria-label": "Select agent",
                children: agents.map((a) => /* @__PURE__ */ jsx6("option", { value: a.id, children: a.name }, a.id))
              }
            )
          ] }),
          /* @__PURE__ */ jsx6("div", { className: "h-8 w-px bg-slate-200 mx-1 hidden sm:block" }),
          /* @__PURE__ */ jsxs5("div", { className: "hidden sm:flex items-center min-w-0", children: [
            /* @__PURE__ */ jsxs5("div", { className: "relative group min-w-0", children: [
              /* @__PURE__ */ jsxs5("div", { className: "flex items-center gap-2 cursor-pointer py-1.5 px-2 rounded hover:bg-slate-50 min-w-0", children: [
                /* @__PURE__ */ jsx6("div", { className: "p-1.5 bg-slate-100 rounded text-slate-500 group-hover:text-purple-600 group-hover:bg-purple-50 transition-colors", children: /* @__PURE__ */ jsx6(Cpu, { size: 15 }) }),
                /* @__PURE__ */ jsxs5("div", { className: "flex flex-col min-w-0", children: [
                  /* @__PURE__ */ jsx6("span", { className: "text-[10px] font-bold text-slate-400 uppercase tracking-wider leading-none mb-0.5", children: "Model" }),
                  /* @__PURE__ */ jsx6("span", { className: "text-xs font-medium text-slate-700 truncate max-w-[260px]", children: effectiveModel ? formatModelLabel(effectiveModel) : "\u2014" })
                ] }),
                /* @__PURE__ */ jsx6("span", { className: clsx4("ml-2 px-1.5 py-0.5 rounded border text-[10px] font-bold", modelTag.cls), children: modelTag.text }),
                /* @__PURE__ */ jsx6(ChevronDown, { size: 12, className: "text-slate-300 ml-2 shrink-0" })
              ] }),
              /* @__PURE__ */ jsxs5(
                "select",
                {
                  className: "absolute inset-0 w-full h-full opacity-0 cursor-pointer",
                  value: activeModelId,
                  onChange: (e) => onModelChange(e.target.value),
                  "aria-label": "Select model",
                  children: [
                    /* @__PURE__ */ jsx6("option", { value: "", children: "Default (Agent)" }),
                    models.filter((m) => !!m.id).map((m) => /* @__PURE__ */ jsx6("option", { value: m.id, children: formatModelLabel(m) }, m.id))
                  ]
                }
              )
            ] }),
            /* @__PURE__ */ jsxs5(
              "button",
              {
                type: "button",
                onClick: () => setIsModelSettingsOpen(true),
                className: "ml-1 p-1.5 rounded hover:bg-slate-50 text-slate-500 hover:text-slate-900 flex items-center gap-2",
                title: "Model settings (override)",
                children: [
                  /* @__PURE__ */ jsx6(SlidersHorizontal2, { size: 15 }),
                  /* @__PURE__ */ jsx6("span", { className: clsx4("hidden xl:inline px-1.5 py-0.5 rounded border text-[10px] font-bold", settingsTag.cls), children: settingsTag.text })
                ]
              }
            ),
            onOpenAgentPanel && /* @__PURE__ */ jsx6(
              "button",
              {
                type: "button",
                onClick: onOpenAgentPanel,
                className: "ml-1 px-2 py-1 rounded border border-slate-200 text-[11px] font-bold text-slate-600 hover:bg-slate-50",
                title: "Open agent settings (overrides are not saved until you apply them there)",
                children: "Agent\u2026"
              }
            )
          ] }),
          /* @__PURE__ */ jsxs5(
            "button",
            {
              type: "button",
              onClick: onOpenContextDetails,
              className: clsx4(
                "hidden lg:flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 transition-colors",
                !contextUsage && "opacity-60 cursor-default"
              ),
              title: "Context usage",
              disabled: !contextUsage,
              children: [
                /* @__PURE__ */ jsx6("span", { className: "text-[10px] font-bold text-slate-400 uppercase tracking-wider", children: "Context" }),
                /* @__PURE__ */ jsx6("span", { className: clsx4("text-xs font-mono", contextTone), children: contextText || "\u2014" })
              ]
            }
          ),
          onOpenHistory && /* @__PURE__ */ jsxs5(
            "button",
            {
              type: "button",
              onClick: onOpenHistory,
              className: "hidden lg:flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 transition-colors text-slate-500",
              title: "Open history",
              children: [
                /* @__PURE__ */ jsx6(Clock, { size: 15 }),
                /* @__PURE__ */ jsx6("span", { className: "text-xs font-semibold", children: "History" })
              ]
            }
          )
        ] }),
        /* @__PURE__ */ jsxs5("div", { className: "flex items-center gap-2 shrink-0", children: [
          onToggleInjectComments && /* @__PURE__ */ jsxs5(
            "button",
            {
              type: "button",
              onClick: onToggleInjectComments,
              className: clsx4(
                "hidden md:flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold border rounded-md transition-all active:scale-95",
                injectCommentsEnabled ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-white text-slate-600 border-slate-200 hover:border-amber-300 hover:text-amber-700 hover:shadow-sm"
              ),
              title: "If enabled, unresolved comments will be included in the next message",
              children: [
                /* @__PURE__ */ jsx6(MessageSquare3, { size: 14 }),
                injectCommentsEnabled ? "Comments: ON" : "Comments: OFF"
              ]
            }
          ),
          /* @__PURE__ */ jsxs5(
            "button",
            {
              type: "button",
              onClick: onOpenCostDetails,
              disabled: !conversationId,
              className: clsx4(
                "hidden md:flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold border rounded-md transition-all active:scale-95",
                "bg-white text-slate-600 border-slate-200 hover:border-emerald-300 hover:text-emerald-700 hover:shadow-sm",
                !conversationId && "opacity-50 cursor-not-allowed"
              ),
              title: "Conversation cost (click for details)",
              children: [
                /* @__PURE__ */ jsx6(DollarSign, { size: 14, className: clsx4(isCostLoading ? "opacity-50" : "") }),
                /* @__PURE__ */ jsx6("span", { className: "font-mono", children: isCostLoading ? "\u2026" : fmtUsd(costUsd || 0) })
              ]
            }
          ),
          /* @__PURE__ */ jsxs5(
            "button",
            {
              onClick: handleShare,
              disabled: isSharing || !conversationId,
              className: clsx4(
                "hidden md:flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold border rounded-md transition-all active:scale-95",
                shareSuccess ? "bg-emerald-50 text-emerald-600 border-emerald-200" : "bg-white text-slate-600 border-slate-200 hover:border-blue-300 hover:text-blue-600 hover:shadow-sm",
                !conversationId && "opacity-50 cursor-not-allowed"
              ),
              title: !conversationId ? "Select a conversation to share" : "Copy share link",
              children: [
                isSharing ? /* @__PURE__ */ jsx6(Loader2, { size: 14, className: "animate-spin" }) : shareSuccess ? /* @__PURE__ */ jsx6(Check2, { size: 14 }) : /* @__PURE__ */ jsx6(Share2, { size: 14 }),
                shareSuccess ? "Copied!" : "Share"
              ]
            }
          ),
          /* @__PURE__ */ jsx6("div", { className: "h-6 w-px bg-slate-200 mx-1" }),
          /* @__PURE__ */ jsxs5(
            "button",
            {
              onClick: onToggleSettings,
              className: clsx4(
                "p-2 rounded-md transition-all duration-200 relative",
                isSettingsOpen ? "bg-slate-100 text-blue-600 ring-1 ring-slate-200" : "text-slate-400 hover:text-slate-700 hover:bg-slate-100"
              ),
              title: "Toggle Settings",
              children: [
                /* @__PURE__ */ jsx6(Settings2, { size: 19 }),
                isSettingsOpen && /* @__PURE__ */ jsx6("span", { className: "absolute top-1.5 right-1.5 w-2 h-2 bg-blue-500 rounded-full border border-white" })
              ]
            }
          )
        ] }),
        /* @__PURE__ */ jsx6(
          ModelSettingsPopover,
          {
            open: isModelSettingsOpen,
            onClose: () => setIsModelSettingsOpen(false),
            providerHint: effectiveModel?.provider || "",
            value: modelSettings,
            onChange: onModelSettingsChange,
            onSaveToAgent: onOpenAgentPanel
          }
        )
      ]
    }
  );
};

// src/studio/components/StudioTabs.tsx
import { Plus as Plus3 } from "lucide-react";
import clsx9 from "clsx";

// src/studio/components/tabs/TabBarDnD.tsx
import clsx6 from "clsx";
import { DndContext, closestCenter } from "@dnd-kit/core";
import { SortableContext, useSortable, rectSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// src/studio/components/tabs/useTabSizer.ts
import { useCallback, useEffect, useMemo as useMemo3, useRef, useState as useState5 } from "react";

// src/studio/components/tabs/TabConstants.ts
var TAB_MIN_W = 56;
var TAB_MAX_W = 240;
var TABS_BAR_H = 40;
var TAB_GAP = 4;
function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}
var GROUP_COLOR_CLASSES = {
  slate: { dot: "bg-slate-500", bg: "bg-slate-50", text: "text-slate-700", ring: "ring-slate-200" },
  blue: { dot: "bg-blue-500", bg: "bg-blue-50", text: "text-blue-700", ring: "ring-blue-200" },
  sky: { dot: "bg-sky-500", bg: "bg-sky-50", text: "text-sky-700", ring: "ring-sky-200" },
  cyan: { dot: "bg-cyan-500", bg: "bg-cyan-50", text: "text-cyan-700", ring: "ring-cyan-200" },
  teal: { dot: "bg-teal-500", bg: "bg-teal-50", text: "text-teal-700", ring: "ring-teal-200" },
  emerald: { dot: "bg-emerald-500", bg: "bg-emerald-50", text: "text-emerald-700", ring: "ring-emerald-200" },
  green: { dot: "bg-green-500", bg: "bg-green-50", text: "text-green-700", ring: "ring-green-200" },
  lime: { dot: "bg-lime-500", bg: "bg-lime-50", text: "text-lime-700", ring: "ring-lime-200" },
  yellow: { dot: "bg-yellow-500", bg: "bg-yellow-50", text: "text-yellow-700", ring: "ring-yellow-200" },
  amber: { dot: "bg-amber-500", bg: "bg-amber-50", text: "text-amber-700", ring: "ring-amber-200" },
  orange: { dot: "bg-orange-500", bg: "bg-orange-50", text: "text-orange-700", ring: "ring-orange-200" },
  red: { dot: "bg-red-500", bg: "bg-red-50", text: "text-red-700", ring: "ring-red-200" },
  rose: { dot: "bg-rose-500", bg: "bg-rose-50", text: "text-rose-700", ring: "ring-rose-200" },
  pink: { dot: "bg-pink-500", bg: "bg-pink-50", text: "text-pink-700", ring: "ring-pink-200" },
  fuchsia: { dot: "bg-fuchsia-500", bg: "bg-fuchsia-50", text: "text-fuchsia-700", ring: "ring-fuchsia-200" },
  purple: { dot: "bg-purple-500", bg: "bg-purple-50", text: "text-purple-700", ring: "ring-purple-200" },
  violet: { dot: "bg-violet-500", bg: "bg-violet-50", text: "text-violet-700", ring: "ring-violet-200" },
  indigo: { dot: "bg-indigo-500", bg: "bg-indigo-50", text: "text-indigo-700", ring: "ring-indigo-200" }
};

// src/studio/components/tabs/useTabSizer.ts
function useTabSizer(opts) {
  const {
    visibleCount,
    rightGutterPx = 44,
    gapPx = TAB_GAP,
    minWidthPx = TAB_MIN_W,
    maxWidthPx = TAB_MAX_W,
    containerPaddingPx = 0
  } = opts;
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState5(0);
  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const cr = entry.contentRect;
        setContainerWidth(Math.max(0, cr.width));
      }
    });
    ro.observe(el);
    return () => {
      try {
        ro.disconnect();
      } catch {
      }
    };
  }, []);
  const recompute = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setContainerWidth(Math.max(0, rect.width));
  }, []);
  const { tabWidth, availableWidth, overflowCount, fitsCount } = useMemo3(() => {
    const cw = Math.max(0, containerWidth - containerPaddingPx);
    const count = Math.max(0, visibleCount);
    const totalGaps = count > 0 ? (count - 1) * gapPx : 0;
    const available = Math.max(0, cw - rightGutterPx - totalGaps);
    if (count <= 0 || available <= 0) {
      return { tabWidth: minWidthPx, availableWidth: available, overflowCount: 0, fitsCount: 0 };
    }
    const ideal = Math.floor(available / count);
    const width = clamp(ideal, minWidthPx, maxWidthPx);
    let overflow = 0;
    let fits = count;
    if (width === minWidthPx) {
      const oneSlot = minWidthPx + gapPx;
      fits = Math.max(1, Math.floor((available + gapPx) / oneSlot));
      overflow = Math.max(0, count - fits);
    }
    return { tabWidth: width, availableWidth: available, overflowCount: overflow, fitsCount: fits };
  }, [containerWidth, containerPaddingPx, rightGutterPx, visibleCount, gapPx, minWidthPx, maxWidthPx]);
  return {
    containerRef,
    containerWidth,
    availableWidth,
    tabWidth,
    overflowCount,
    fitsCount,
    recompute
  };
}

// src/studio/components/tabs/TabItem.tsx
import clsx5 from "clsx";
import { X as X4 } from "lucide-react";
import { jsx as jsx7, jsxs as jsxs6 } from "react/jsx-runtime";
var TabItem = ({
  id,
  title,
  isActive,
  width,
  onSelect,
  onClose,
  dragProps,
  hideCloseUntilHover = true,
  className
}) => {
  const handleAux = (e) => {
    if (e.button === 1) {
      e.preventDefault();
      e.stopPropagation();
      onClose(id);
    }
  };
  return /* @__PURE__ */ jsx7(
    "div",
    {
      className: clsx5("h-full", className),
      style: { width },
      children: /* @__PURE__ */ jsxs6(
        "button",
        {
          type: "button",
          ...dragProps?.attributes || {},
          ...dragProps?.listeners || {},
          onClick: () => onSelect(id),
          onAuxClick: handleAux,
          className: clsx5(
            "group relative w-full h-full flex items-center gap-2 px-3 text-xs border-r border-slate-200 select-none",
            "transition-colors",
            isActive ? "bg-white text-blue-600 font-semibold border-t-2 border-t-blue-500" : "bg-slate-100 text-slate-600 hover:bg-slate-50 border-t-2 border-t-transparent"
          ),
          title,
          children: [
            /* @__PURE__ */ jsx7("span", { className: "truncate flex-1 text-left", children: title || "Untitled" }),
            /* @__PURE__ */ jsx7(
              "span",
              {
                className: clsx5(
                  "shrink-0",
                  hideCloseUntilHover ? isActive ? "opacity-100" : "opacity-0 group-hover:opacity-100" : "opacity-100"
                ),
                children: /* @__PURE__ */ jsx7(
                  "button",
                  {
                    type: "button",
                    onClick: (e) => {
                      e.stopPropagation();
                      onClose(id);
                    },
                    className: clsx5(
                      "p-0.5 rounded-md hover:bg-slate-200 text-slate-400 hover:text-red-500 transition-colors"
                    ),
                    "aria-label": "Close tab",
                    title: "Close tab",
                    children: /* @__PURE__ */ jsx7(X4, { size: 12 })
                  }
                )
              }
            )
          ]
        }
      )
    }
  );
};

// src/studio/components/tabs/TabBarDnD.tsx
import { jsx as jsx8, jsxs as jsxs7 } from "react/jsx-runtime";
var TabBarDnD = ({
  items,
  activeId,
  onSelect,
  onClose,
  onReorder,
  rightSlot,
  rightSlotWidthPx = 44,
  className,
  hideCloseUntilHover = true
}) => {
  const { containerRef, tabWidth } = useTabSizer({
    visibleCount: items.length,
    rightGutterPx: rightSlot ? rightSlotWidthPx : 0,
    gapPx: TAB_GAP
  });
  const handleDragEnd = (evt) => {
    const { active, over } = evt;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((t) => t.id === active.id);
    const newIndex = items.findIndex((t) => t.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    if (oldIndex === newIndex) return;
    onReorder(oldIndex, newIndex);
  };
  return /* @__PURE__ */ jsxs7("div", { className: clsx6("relative w-full h-10 border-b border-slate-200 bg-slate-100", className), children: [
    /* @__PURE__ */ jsx8(
      "div",
      {
        ref: containerRef,
        className: "absolute left-0 top-0 bottom-0 right-0 pr-[var(--right-slot-w,0px)]",
        style: { ["--right-slot-w"]: `${rightSlot ? rightSlotWidthPx : 0}px` },
        children: /* @__PURE__ */ jsx8(DndContext, { collisionDetection: closestCenter, onDragEnd: handleDragEnd, children: /* @__PURE__ */ jsx8(SortableContext, { items: items.map((t) => t.id), strategy: rectSortingStrategy, children: /* @__PURE__ */ jsx8(
          "div",
          {
            className: "flex items-stretch h-full",
            style: { columnGap: `${TAB_GAP}px`, height: `${TABS_BAR_H}px` },
            children: items.map((tab) => /* @__PURE__ */ jsx8(
              SortableTab,
              {
                tab,
                width: tabWidth,
                active: tab.id === activeId,
                onSelect,
                onClose,
                hideCloseUntilHover
              },
              tab.id
            ))
          }
        ) }) })
      }
    ),
    rightSlot && /* @__PURE__ */ jsx8(
      "div",
      {
        className: "absolute top-0 bottom-0 right-0 flex items-center justify-center border-l border-slate-200 bg-slate-100",
        style: { width: rightSlotWidthPx },
        children: rightSlot
      }
    )
  ] });
};
var SortableTab = ({ tab, width, active, onSelect, onClose, hideCloseUntilHover = true }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: tab.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    width,
    opacity: isDragging ? 0.8 : 1
  };
  return /* @__PURE__ */ jsx8("div", { ref: setNodeRef, style, children: /* @__PURE__ */ jsx8(
    TabItem,
    {
      id: tab.id,
      title: tab.title,
      isActive: active,
      width,
      onSelect,
      onClose,
      dragProps: { attributes, listeners, isDragging },
      hideCloseUntilHover
    }
  ) });
};

// src/studio/components/tabs/TabGroupBar.tsx
import { useMemo as useMemo5 } from "react";
import clsx8 from "clsx";
import { DndContext as DndContext2, closestCenter as closestCenter2 } from "@dnd-kit/core";
import { SortableContext as SortableContext2, rectSortingStrategy as rectSortingStrategy2, useSortable as useSortable2 } from "@dnd-kit/sortable";
import { CSS as CSS2 } from "@dnd-kit/utilities";

// src/studio/components/tabs/TabGroupHeader.tsx
import { useState as useState6 } from "react";
import clsx7 from "clsx";
import { ChevronDown as ChevronDown2, MoreVertical, Plus as Plus2 } from "lucide-react";
import { jsx as jsx9, jsxs as jsxs8 } from "react/jsx-runtime";
var TabGroupHeader = ({
  id,
  name,
  color = "slate",
  collapsed,
  onToggleCollapsed,
  onRename,
  onChangeColor,
  onDelete,
  onNewTab,
  className
}) => {
  const palette = GROUP_COLOR_CLASSES[color || "slate"] || GROUP_COLOR_CLASSES.slate;
  const [editing, setEditing] = useState6(false);
  const [val, setVal] = useState6(name);
  const commit = () => {
    setEditing(false);
    const next = val.trim() || "Group";
    if (next !== name) onRename?.(next);
  };
  return /* @__PURE__ */ jsxs8("div", { className: clsx7("flex items-center h-10 pl-2 pr-1 mr-1 rounded-md", className), children: [
    /* @__PURE__ */ jsxs8(
      "button",
      {
        type: "button",
        onClick: () => onToggleCollapsed(!collapsed),
        className: clsx7(
          "flex items-center gap-2 px-2 py-1 rounded hover:bg-slate-200/60 transition-colors"
        ),
        title: collapsed ? "Expand group" : "Collapse group",
        children: [
          /* @__PURE__ */ jsx9("span", { className: clsx7("w-2 h-2 rounded-full shrink-0", palette.dot) }),
          editing ? /* @__PURE__ */ jsx9(
            "input",
            {
              value: val,
              onChange: (e) => setVal(e.target.value),
              onBlur: commit,
              onKeyDown: (e) => {
                if (e.key === "Enter") commit();
                if (e.key === "Escape") {
                  setEditing(false);
                  setVal(name);
                }
              },
              autoFocus: true,
              className: "text-xs font-semibold border border-slate-300 rounded px-1 py-0.5 bg-white w-28 outline-none"
            }
          ) : /* @__PURE__ */ jsx9("span", { className: clsx7("text-xs font-semibold whitespace-nowrap", palette.text), onDoubleClick: () => setEditing(true), children: name }),
          /* @__PURE__ */ jsx9(
            ChevronDown2,
            {
              size: 14,
              className: clsx7("text-slate-500 transition-transform", collapsed ? "-rotate-90" : "rotate-0")
            }
          )
        ]
      }
    ),
    /* @__PURE__ */ jsxs8("div", { className: "ml-1 flex items-center gap-1", children: [
      onNewTab && /* @__PURE__ */ jsx9(
        "button",
        {
          type: "button",
          onClick: () => onNewTab?.(),
          className: "p-1 rounded hover:bg-slate-200/60 text-slate-600",
          title: "New tab in group",
          children: /* @__PURE__ */ jsx9(Plus2, { size: 14 })
        }
      ),
      /* @__PURE__ */ jsx9("div", { className: "relative", children: /* @__PURE__ */ jsx9(
        "button",
        {
          type: "button",
          className: "p-1 rounded hover:bg-slate-200/60 text-slate-600",
          title: "Group options",
          children: /* @__PURE__ */ jsx9(MoreVertical, { size: 14 })
        }
      ) })
    ] })
  ] });
};

// src/studio/components/tabs/TabGroupBar.tsx
import { jsx as jsx10, jsxs as jsxs9 } from "react/jsx-runtime";
var TabGroupBar = ({
  ungroupedTabIds,
  groups,
  tabMeta,
  activeTabId,
  onSelectTab,
  onCloseTab,
  onMoveTab,
  onCreateGroup,
  onRenameGroup,
  onChangeGroupColor,
  onToggleGroupCollapsed,
  onDeleteGroup,
  onCreateTabInGroup,
  rightSlot,
  rightSlotWidthPx = 44,
  className
}) => {
  const visibleTabCount = useMemo5(() => {
    const groupVisible = groups.reduce((acc, g) => acc + (g.collapsed ? 0 : g.tabIds.length), 0);
    return ungroupedTabIds.length + groupVisible;
  }, [ungroupedTabIds.length, groups]);
  const { containerRef, tabWidth } = useTabSizer({
    visibleCount: visibleTabCount,
    rightGutterPx: rightSlot ? rightSlotWidthPx : 0,
    gapPx: TAB_GAP
  });
  const handleDragEnd = (evt) => {
    const { active, over } = evt;
    if (!over || active.id === over.id) return;
    const activeId = String(active.id);
    const overId = String(over.id);
    const containers = [
      { id: null, items: ungroupedTabIds },
      ...groups.filter((g) => !g.collapsed).map((g) => ({ id: g.id, items: g.tabIds }))
    ];
    const src = containers.find((c) => c.items.includes(activeId));
    const dst = containers.find((c) => c.items.includes(overId));
    if (!src || !dst) return;
    const newIndex = dst.items.indexOf(overId);
    onMoveTab(activeId, dst.id, newIndex);
  };
  return /* @__PURE__ */ jsxs9("div", { className: clsx8("relative w-full border-b border-slate-200 bg-slate-100", className), style: { height: `${TABS_BAR_H}px` }, children: [
    /* @__PURE__ */ jsx10(
      "div",
      {
        ref: containerRef,
        className: "absolute left-0 top-0 bottom-0 right-0 pr-[var(--right-slot-w,0px)]",
        style: { ["--right-slot-w"]: `${rightSlot ? rightSlotWidthPx : 0}px` },
        children: /* @__PURE__ */ jsx10(DndContext2, { collisionDetection: closestCenter2, onDragEnd: handleDragEnd, children: /* @__PURE__ */ jsxs9("div", { className: "flex items-stretch h-full overflow-hidden", style: { columnGap: `${TAB_GAP}px` }, children: [
          ungroupedTabIds.length > 0 && /* @__PURE__ */ jsx10(Container, { id: "ungrouped", items: ungroupedTabIds, children: ungroupedTabIds.map((tid) => /* @__PURE__ */ jsx10(
            SortableTab2,
            {
              id: tid,
              width: tabWidth,
              active: tid === activeTabId,
              title: tabMeta[tid]?.title || "Untitled",
              onSelect: onSelectTab,
              onClose: onCloseTab
            },
            tid
          )) }),
          groups.map((g) => /* @__PURE__ */ jsxs9("div", { className: "flex items-stretch", children: [
            /* @__PURE__ */ jsx10(
              TabGroupHeader,
              {
                id: g.id,
                name: g.name,
                color: g.color,
                collapsed: !!g.collapsed,
                onToggleCollapsed: (collapsed) => onToggleGroupCollapsed?.(g.id, collapsed),
                onRename: (name) => onRenameGroup?.(g.id, name),
                onChangeColor: (color) => onChangeGroupColor?.(g.id, color),
                onDelete: () => onDeleteGroup?.(g.id),
                onNewTab: () => onCreateTabInGroup?.(g.id)
              }
            ),
            !g.collapsed && /* @__PURE__ */ jsx10(Container, { id: `group:${g.id}`, items: g.tabIds, children: g.tabIds.map((tid) => /* @__PURE__ */ jsx10(
              SortableTab2,
              {
                id: tid,
                width: tabWidth,
                active: tid === activeTabId,
                title: tabMeta[tid]?.title || "Untitled",
                onSelect: onSelectTab,
                onClose: onCloseTab
              },
              tid
            )) })
          ] }, g.id)),
          onCreateGroup && /* @__PURE__ */ jsx10(
            "button",
            {
              type: "button",
              onClick: onCreateGroup,
              className: "ml-1 px-2 text-xs font-bold text-slate-600 rounded hover:bg-slate-200",
              title: "New group",
              children: "+ Group"
            }
          )
        ] }) })
      }
    ),
    rightSlot && /* @__PURE__ */ jsx10(
      "div",
      {
        className: "absolute top-0 bottom-0 right-0 flex items-center justify-center border-l border-slate-200 bg-slate-100",
        style: { width: rightSlotWidthPx },
        children: rightSlot
      }
    )
  ] });
};
var Container = ({ id, items, children }) => {
  return /* @__PURE__ */ jsx10(SortableContext2, { items, strategy: rectSortingStrategy2, children: /* @__PURE__ */ jsx10("div", { className: "flex items-stretch h-full", style: { columnGap: `${TAB_GAP}px` }, children }) });
};
var SortableTab2 = ({ id, title, width, active, onSelect, onClose }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable2({ id });
  const style = {
    transform: CSS2.Transform.toString(transform),
    transition,
    width,
    opacity: isDragging ? 0.85 : 1
  };
  return /* @__PURE__ */ jsx10("div", { ref: setNodeRef, style, children: /* @__PURE__ */ jsx10(
    TabItem,
    {
      id,
      title,
      width,
      isActive: active,
      onSelect,
      onClose,
      dragProps: { attributes, listeners, isDragging }
    }
  ) });
};

// src/studio/components/StudioTabs.tsx
import { jsx as jsx11 } from "react/jsx-runtime";
var StudioTabs = ({
  tabs,
  onReorderTabs,
  tabGroups,
  ungroupedTabIds,
  tabMeta,
  onMoveTab,
  onCreateGroup,
  onRenameGroup,
  onChangeGroupColor,
  onToggleGroupCollapsed,
  onDeleteGroup,
  onCreateTabInGroup,
  activeTabId,
  onSelectTab,
  onCloseTab,
  onNewChat,
  className
}) => {
  const rightButton = /* @__PURE__ */ jsx11(
    "button",
    {
      onClick: onNewChat,
      className: "h-full w-10 text-slate-600 hover:text-blue-600 hover:bg-slate-50 active:bg-slate-200 transition-colors flex items-center justify-center",
      title: "New Chat (Ctrl/Cmd+T)",
      "aria-label": "New Chat",
      children: /* @__PURE__ */ jsx11(Plus3, { size: 16 })
    }
  );
  const isGrouped = Array.isArray(tabGroups) && tabGroups.length > 0 || Array.isArray(ungroupedTabIds) && (ungroupedTabIds?.length || 0) > 0;
  if (isGrouped && tabGroups && typeof ungroupedTabIds !== "undefined" && tabMeta) {
    return /* @__PURE__ */ jsx11(
      TabGroupBar,
      {
        ungroupedTabIds,
        groups: tabGroups,
        tabMeta,
        activeTabId,
        onSelectTab,
        onCloseTab,
        onMoveTab: (tabId, containerId, idx) => onMoveTab?.(tabId, containerId, idx),
        onCreateGroup,
        onRenameGroup,
        onChangeGroupColor,
        onToggleGroupCollapsed,
        onDeleteGroup,
        onCreateTabInGroup,
        rightSlot: rightButton,
        rightSlotWidthPx: 40,
        className
      }
    );
  }
  const items = (tabs || []).map((t) => ({ id: t.id, title: t.title || "Untitled" }));
  return /* @__PURE__ */ jsx11(
    TabBarDnD,
    {
      items,
      activeId: activeTabId,
      onSelect: onSelectTab,
      onClose: onCloseTab,
      onReorder: (a, b) => onReorderTabs?.(a, b),
      rightSlot: rightButton,
      rightSlotWidthPx: 40,
      className: clsx9("bg-slate-100", className),
      hideCloseUntilHover: true
    }
  );
};

// src/studio/components/ContextUsageModal.tsx
import clsx10 from "clsx";
import { X as X5 } from "lucide-react";
import { Fragment, jsx as jsx12, jsxs as jsxs10 } from "react/jsx-runtime";
function ContextUsageModal({
  open,
  onClose,
  usage
}) {
  if (!open) return null;
  return /* @__PURE__ */ jsxs10("div", { className: "fixed inset-0 z-50", children: [
    /* @__PURE__ */ jsx12("button", { className: "absolute inset-0 bg-black/40", onClick: onClose, "aria-label": "Close overlay" }),
    /* @__PURE__ */ jsxs10("div", { className: "absolute right-4 top-4 bottom-4 w-[520px] max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden flex flex-col", children: [
      /* @__PURE__ */ jsxs10("div", { className: "px-4 py-3 border-b border-slate-200 flex items-center justify-between", children: [
        /* @__PURE__ */ jsxs10("div", { children: [
          /* @__PURE__ */ jsx12("div", { className: "text-sm font-bold text-slate-900", children: "Context usage" }),
          /* @__PURE__ */ jsx12("div", { className: "text-xs text-slate-500", children: "Tokens in the context window (history + files + draft)" })
        ] }),
        /* @__PURE__ */ jsx12("button", { onClick: onClose, className: "p-2 rounded hover:bg-slate-100", "aria-label": "Close", children: /* @__PURE__ */ jsx12(X5, { size: 16 }) })
      ] }),
      /* @__PURE__ */ jsx12("div", { className: "p-4 space-y-3 overflow-auto", children: !usage ? /* @__PURE__ */ jsx12("div", { className: "text-sm text-slate-500", children: "No data yet." }) : /* @__PURE__ */ jsxs10(Fragment, { children: [
        /* @__PURE__ */ jsxs10("div", { className: "grid grid-cols-2 gap-3 text-sm", children: [
          /* @__PURE__ */ jsxs10("div", { className: "p-3 rounded border border-slate-200", children: [
            /* @__PURE__ */ jsx12("div", { className: "text-xs text-slate-500", children: "Total" }),
            /* @__PURE__ */ jsx12("div", { className: "font-mono font-semibold", children: usage.total_tokens.toLocaleString() })
          ] }),
          /* @__PURE__ */ jsxs10("div", { className: clsx10("p-3 rounded border", usage.is_limit_exceeded ? "border-rose-200 bg-rose-50" : "border-slate-200"), children: [
            /* @__PURE__ */ jsx12("div", { className: "text-xs text-slate-500", children: "Limit" }),
            /* @__PURE__ */ jsx12("div", { className: "font-mono font-semibold", children: usage.model_context_limit.toLocaleString() })
          ] }),
          /* @__PURE__ */ jsxs10("div", { className: "p-3 rounded border border-slate-200", children: [
            /* @__PURE__ */ jsx12("div", { className: "text-xs text-slate-500", children: "Files (est.)" }),
            /* @__PURE__ */ jsx12("div", { className: "font-mono font-semibold", children: (usage.file_tokens_est || 0).toLocaleString() })
          ] }),
          /* @__PURE__ */ jsxs10("div", { className: "p-3 rounded border border-slate-200", children: [
            /* @__PURE__ */ jsx12("div", { className: "text-xs text-slate-500", children: "Updated" }),
            /* @__PURE__ */ jsx12("div", { className: "text-xs", children: new Date(usage.updatedAt).toLocaleString() })
          ] })
        ] }),
        /* @__PURE__ */ jsxs10("details", { className: "mt-2", children: [
          /* @__PURE__ */ jsx12("summary", { className: "cursor-pointer text-sm font-semibold text-slate-700", children: "Breakdown (preview)" }),
          /* @__PURE__ */ jsx12("div", { className: "mt-2 space-y-2", children: usage.breakdown?.map((b, idx) => /* @__PURE__ */ jsxs10("div", { className: "p-2 rounded border border-slate-200", children: [
            /* @__PURE__ */ jsxs10("div", { className: "flex items-center justify-between text-xs text-slate-500", children: [
              /* @__PURE__ */ jsxs10("span", { children: [
                b.role,
                " #",
                b.index
              ] }),
              /* @__PURE__ */ jsxs10("span", { className: "font-mono", children: [
                b.tokens.toLocaleString(),
                " t"
              ] })
            ] }),
            /* @__PURE__ */ jsx12("div", { className: "text-xs text-slate-700", children: b.preview })
          ] }, idx)) })
        ] }),
        /* @__PURE__ */ jsx12("div", { className: "text-xs text-slate-500", children: "Next milestone: replace this modal with a full side panel + conversation minimap per message part." })
      ] }) })
    ] })
  ] });
}

// src/studio/components/ConversationCostModal.tsx
import { useMemo as useMemo6, useState as useState7 } from "react";
import clsx11 from "clsx";
import { X as X6, ChevronDown as ChevronDown3 } from "lucide-react";
import { Fragment as Fragment2, jsx as jsx13, jsxs as jsxs11 } from "react/jsx-runtime";
function fmtUsd2(n, digits = 4) {
  const v = Number(n || 0);
  if (!Number.isFinite(v)) return "$0.0000";
  return `$${v.toFixed(digits)}`;
}
function fmtNum(n, digits = 4) {
  const v = Number(n || 0);
  if (!Number.isFinite(v)) return "0";
  return v.toFixed(digits);
}
function fmtInt(n) {
  const v = Number(n || 0);
  if (!Number.isFinite(v)) return "0";
  return Math.round(v).toLocaleString();
}
function pct(n) {
  if (n === null || n === void 0 || !Number.isFinite(Number(n))) return "\u2014";
  return `${Number(n).toFixed(1)}%`;
}
function safeModelName(s) {
  const v = (s || "").trim();
  return v || "\u2014";
}
function inferImpactPct(items) {
  for (const it of items) {
    if (it.impact_pct != null) return Number(it.impact_pct);
  }
  return 33;
}
function inferMarginPct(items) {
  for (const it of items) {
    if (it.margin_pct != null) return Number(it.margin_pct);
  }
  return 0;
}
function sum(items, key) {
  return items.reduce((acc, it) => acc + Number(it[key] || 0), 0);
}
function compare(a, b) {
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
}
function ConversationCostModal({
  open,
  onClose,
  data,
  isLoading,
  error
}) {
  const [expanded, setExpanded] = useState7({});
  const [sortKey, setSortKey] = useState7("time");
  const [sortDir, setSortDir] = useState7("asc");
  const items = data?.items || [];
  const totals = useMemo6(() => {
    const provider = sum(items, "actual_provider_cost_usd");
    const impact = sum(items, "impact_usd");
    const margin = sum(items, "margin_usd");
    const user = sum(items, "total_cost_usd");
    const userFallback = user > 0 ? user : provider * (1 + inferImpactPct(items) / 100 + inferMarginPct(items) / 100);
    return {
      provider_cost_usd: provider,
      impact_usd: impact,
      margin_usd: margin,
      total_cost_usd: data?.total_cost_usd ?? userFallback,
      credits_charged: data?.total_credits_charged ?? 0,
      total_tokens: data?.total_tokens ?? sum(items, "total_tokens"),
      runs: items.length,
      impact_pct: inferImpactPct(items),
      margin_pct: inferMarginPct(items)
    };
  }, [data?.total_cost_usd, data?.total_credits_charged, data?.total_tokens, items]);
  const sortedItems = useMemo6(() => {
    const copy = [...items];
    const dir = sortDir === "asc" ? 1 : -1;
    copy.sort((a, b) => {
      if (sortKey === "time") return dir * compare(new Date(a.timestamp).getTime(), new Date(b.timestamp).getTime());
      if (sortKey === "model") return dir * compare(safeModelName(a.model_name), safeModelName(b.model_name));
      if (sortKey === "tokens") return dir * compare(a.total_tokens || 0, b.total_tokens || 0);
      if (sortKey === "user_cost") return dir * compare(a.total_cost_usd || 0, b.total_cost_usd || 0);
      if (sortKey === "provider_cost") return dir * compare(a.actual_provider_cost_usd || 0, b.actual_provider_cost_usd || 0);
      return 0;
    });
    return copy;
  }, [items, sortDir, sortKey]);
  const setSort = (k) => {
    if (sortKey === k) setSortDir((d) => d === "asc" ? "desc" : "asc");
    else {
      setSortKey(k);
      setSortDir(k === "time" ? "asc" : "desc");
    }
  };
  const toggleExpanded = (id) => {
    setExpanded((p) => ({ ...p, [id]: !p[id] }));
  };
  if (!open) return null;
  return /* @__PURE__ */ jsxs11("div", { className: "fixed inset-0 z-50", children: [
    /* @__PURE__ */ jsx13("button", { className: "absolute inset-0 bg-black/40", onClick: onClose, "aria-label": "Close overlay" }),
    /* @__PURE__ */ jsxs11("div", { className: "absolute right-4 top-4 bottom-4 w-[980px] max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-xl border border-slate-200 overflow-hidden flex flex-col", children: [
      /* @__PURE__ */ jsxs11("div", { className: "px-4 py-3 border-b border-slate-200 flex items-start justify-between gap-3", children: [
        /* @__PURE__ */ jsxs11("div", { className: "min-w-0", children: [
          /* @__PURE__ */ jsx13("div", { className: "text-sm font-bold text-slate-900", children: "Usage & cost" }),
          /* @__PURE__ */ jsxs11("div", { className: "text-xs text-slate-500", children: [
            "User cost = provider + impact (",
            pct(totals.impact_pct),
            ") + margin (",
            pct(totals.margin_pct),
            ")"
          ] })
        ] }),
        /* @__PURE__ */ jsx13("button", { onClick: onClose, className: "p-2 rounded hover:bg-slate-100", "aria-label": "Close", children: /* @__PURE__ */ jsx13(X6, { size: 16 }) })
      ] }),
      /* @__PURE__ */ jsx13("div", { className: "p-4 overflow-auto", children: isLoading ? /* @__PURE__ */ jsx13("div", { className: "text-sm text-slate-500", children: "Loading\u2026" }) : error ? /* @__PURE__ */ jsx13("div", { className: "text-sm text-rose-600", children: error }) : !data ? /* @__PURE__ */ jsx13("div", { className: "text-sm text-slate-500", children: "No data." }) : /* @__PURE__ */ jsxs11(Fragment2, { children: [
        /* @__PURE__ */ jsxs11("div", { className: "grid grid-cols-1 md:grid-cols-4 gap-3 mb-4", children: [
          /* @__PURE__ */ jsxs11("div", { className: "p-3 rounded-lg border border-slate-200 bg-white", children: [
            /* @__PURE__ */ jsx13("div", { className: "text-[11px] font-bold text-slate-500 uppercase", children: "Total user cost" }),
            /* @__PURE__ */ jsx13("div", { className: "text-lg font-mono font-bold text-slate-900", children: fmtUsd2(totals.total_cost_usd) }),
            /* @__PURE__ */ jsxs11("div", { className: "text-[11px] text-slate-400 mt-1", children: [
              "Credits: ",
              /* @__PURE__ */ jsx13("span", { className: "font-mono", children: fmtNum(totals.credits_charged, 4) })
            ] })
          ] }),
          /* @__PURE__ */ jsxs11("div", { className: "p-3 rounded-lg border border-slate-200 bg-white", children: [
            /* @__PURE__ */ jsx13("div", { className: "text-[11px] font-bold text-slate-500 uppercase", children: "Provider cost" }),
            /* @__PURE__ */ jsx13("div", { className: "text-lg font-mono font-bold text-slate-900", children: fmtUsd2(totals.provider_cost_usd) }),
            /* @__PURE__ */ jsx13("div", { className: "text-[11px] text-slate-400 mt-1", children: "What we paid upstream" })
          ] }),
          /* @__PURE__ */ jsxs11("div", { className: "p-3 rounded-lg border border-slate-200 bg-white", children: [
            /* @__PURE__ */ jsx13("div", { className: "text-[11px] font-bold text-slate-500 uppercase", children: "Impact" }),
            /* @__PURE__ */ jsx13("div", { className: "text-lg font-mono font-bold text-slate-900", children: fmtUsd2(totals.impact_usd) }),
            /* @__PURE__ */ jsxs11("div", { className: "text-[11px] text-slate-400 mt-1", children: [
              pct(totals.impact_pct),
              " of provider cost"
            ] })
          ] }),
          /* @__PURE__ */ jsxs11("div", { className: "p-3 rounded-lg border border-slate-200 bg-white", children: [
            /* @__PURE__ */ jsx13("div", { className: "text-[11px] font-bold text-slate-500 uppercase", children: "Tokens" }),
            /* @__PURE__ */ jsx13("div", { className: "text-lg font-mono font-bold text-slate-900", children: fmtInt(totals.total_tokens) }),
            /* @__PURE__ */ jsxs11("div", { className: "text-[11px] text-slate-400 mt-1", children: [
              totals.runs,
              " run",
              totals.runs === 1 ? "" : "s"
            ] })
          ] })
        ] }),
        /* @__PURE__ */ jsxs11("div", { className: "border border-slate-200 rounded-lg overflow-hidden", children: [
          /* @__PURE__ */ jsxs11("div", { className: "grid grid-cols-14 gap-2 px-3 py-2 bg-slate-50 text-[11px] font-bold text-slate-500 uppercase", children: [
            /* @__PURE__ */ jsxs11("button", { className: "col-span-3 text-left hover:text-slate-800", onClick: () => setSort("time"), children: [
              "Time",
              /* @__PURE__ */ jsx13("span", { className: "ml-1 text-slate-300", children: sortKey === "time" ? sortDir === "asc" ? "\u25B2" : "\u25BC" : "" })
            ] }),
            /* @__PURE__ */ jsxs11("button", { className: "col-span-4 text-left hover:text-slate-800", onClick: () => setSort("model"), children: [
              "Model",
              /* @__PURE__ */ jsx13("span", { className: "ml-1 text-slate-300", children: sortKey === "model" ? sortDir === "asc" ? "\u25B2" : "\u25BC" : "" })
            ] }),
            /* @__PURE__ */ jsxs11("button", { className: "col-span-2 text-right hover:text-slate-800", onClick: () => setSort("tokens"), children: [
              "Tokens",
              /* @__PURE__ */ jsx13("span", { className: "ml-1 text-slate-300", children: sortKey === "tokens" ? sortDir === "asc" ? "\u25B2" : "\u25BC" : "" })
            ] }),
            /* @__PURE__ */ jsxs11("button", { className: "col-span-2 text-right hover:text-slate-800", onClick: () => setSort("provider_cost"), children: [
              "Provider",
              /* @__PURE__ */ jsx13("span", { className: "ml-1 text-slate-300", children: sortKey === "provider_cost" ? sortDir === "asc" ? "\u25B2" : "\u25BC" : "" })
            ] }),
            /* @__PURE__ */ jsx13("div", { className: "col-span-1 text-right", children: "Impact" }),
            /* @__PURE__ */ jsx13("div", { className: "col-span-1 text-right", children: "Margin" }),
            /* @__PURE__ */ jsxs11("button", { className: "col-span-1 text-right hover:text-slate-800", onClick: () => setSort("user_cost"), children: [
              "User",
              /* @__PURE__ */ jsx13("span", { className: "ml-1 text-slate-300", children: sortKey === "user_cost" ? sortDir === "asc" ? "\u25B2" : "\u25BC" : "" })
            ] })
          ] }),
          sortedItems.map((it) => {
            const isOpen = !!expanded[it.id];
            const model = safeModelName(it.model_name);
            const provider = (it.provider || "\u2014").toString();
            const inRate = it.input_pricing_per_million_tokens;
            const outRate = it.output_pricing_per_million_tokens;
            return /* @__PURE__ */ jsxs11("div", { className: "border-t border-slate-200", children: [
              /* @__PURE__ */ jsxs11(
                "button",
                {
                  type: "button",
                  onClick: () => toggleExpanded(it.id),
                  className: "w-full grid grid-cols-14 gap-2 px-3 py-2 text-sm hover:bg-slate-50 text-left",
                  children: [
                    /* @__PURE__ */ jsxs11("div", { className: "col-span-3 text-xs text-slate-600 flex items-center gap-2", children: [
                      /* @__PURE__ */ jsx13(ChevronDown3, { size: 14, className: clsx11("text-slate-400 transition-transform", isOpen && "rotate-180") }),
                      /* @__PURE__ */ jsx13("span", { children: new Date(it.timestamp).toLocaleString(void 0, {
                        month: "short",
                        day: "2-digit",
                        hour: "2-digit",
                        minute: "2-digit"
                      }) })
                    ] }),
                    /* @__PURE__ */ jsxs11("div", { className: "col-span-4 min-w-0", children: [
                      /* @__PURE__ */ jsx13("div", { className: "text-xs text-slate-800 truncate", title: model, children: model }),
                      /* @__PURE__ */ jsxs11("div", { className: "text-[11px] text-slate-400 truncate", children: [
                        provider,
                        it.purpose ? ` \u2022 ${it.purpose}` : ""
                      ] })
                    ] }),
                    /* @__PURE__ */ jsxs11("div", { className: "col-span-2 text-right", children: [
                      /* @__PURE__ */ jsx13("div", { className: "text-xs font-mono text-slate-800", children: fmtInt(it.total_tokens) }),
                      /* @__PURE__ */ jsxs11("div", { className: "text-[11px] font-mono text-slate-400", children: [
                        fmtInt(it.prompt_tokens),
                        " + ",
                        fmtInt(it.completion_tokens)
                      ] })
                    ] }),
                    /* @__PURE__ */ jsxs11("div", { className: "col-span-2 text-right", children: [
                      /* @__PURE__ */ jsx13("div", { className: "text-xs font-mono text-slate-900", children: fmtUsd2(it.actual_provider_cost_usd) }),
                      /* @__PURE__ */ jsx13("div", { className: "text-[11px] font-mono text-slate-400", children: "prov" })
                    ] }),
                    /* @__PURE__ */ jsxs11("div", { className: "col-span-1 text-right", children: [
                      /* @__PURE__ */ jsx13("div", { className: "text-xs font-mono text-slate-900", children: fmtUsd2(it.impact_usd) }),
                      /* @__PURE__ */ jsx13("div", { className: "text-[11px] font-mono text-slate-400", children: pct(it.impact_pct ?? totals.impact_pct) })
                    ] }),
                    /* @__PURE__ */ jsxs11("div", { className: "col-span-1 text-right", children: [
                      /* @__PURE__ */ jsx13("div", { className: "text-xs font-mono text-slate-900", children: fmtUsd2(it.margin_usd) }),
                      /* @__PURE__ */ jsx13("div", { className: "text-[11px] font-mono text-slate-400", children: pct(it.margin_pct ?? totals.margin_pct) })
                    ] }),
                    /* @__PURE__ */ jsxs11("div", { className: "col-span-1 text-right", children: [
                      /* @__PURE__ */ jsx13("div", { className: "text-xs font-mono text-slate-900", children: fmtUsd2(it.total_cost_usd) }),
                      /* @__PURE__ */ jsxs11("div", { className: "text-[11px] font-mono text-slate-400", children: [
                        "cr ",
                        fmtNum(it.credits_charged, 4)
                      ] })
                    ] })
                  ]
                }
              ),
              isOpen && /* @__PURE__ */ jsx13("div", { className: "px-3 pb-3", children: /* @__PURE__ */ jsxs11("div", { className: "mt-1 grid grid-cols-1 md:grid-cols-3 gap-2 text-xs", children: [
                /* @__PURE__ */ jsxs11("div", { className: "p-2 rounded border border-slate-200 bg-white", children: [
                  /* @__PURE__ */ jsx13("div", { className: "text-[11px] font-bold text-slate-500 uppercase", children: "Unit pricing ($/1M)" }),
                  /* @__PURE__ */ jsx13("div", { className: "mt-1 text-slate-700 font-mono", children: inRate != null && outRate != null ? `in ${Number(inRate).toFixed(4)} \u2022 out ${Number(outRate).toFixed(4)}` : "n/a" })
                ] }),
                /* @__PURE__ */ jsxs11("div", { className: "p-2 rounded border border-slate-200 bg-white", children: [
                  /* @__PURE__ */ jsx13("div", { className: "text-[11px] font-bold text-slate-500 uppercase", children: "Effective breakdown" }),
                  /* @__PURE__ */ jsxs11("div", { className: "mt-1 text-slate-700 font-mono", children: [
                    "provider ",
                    fmtUsd2(it.actual_provider_cost_usd),
                    " + impact ",
                    fmtUsd2(it.impact_usd),
                    " + margin ",
                    fmtUsd2(it.margin_usd)
                  ] })
                ] }),
                /* @__PURE__ */ jsxs11("div", { className: "p-2 rounded border border-slate-200 bg-white", children: [
                  /* @__PURE__ */ jsx13("div", { className: "text-[11px] font-bold text-slate-500 uppercase", children: "Metadata" }),
                  /* @__PURE__ */ jsx13("div", { className: "mt-1 text-slate-600", children: it.metadata && Object.keys(it.metadata).length > 0 ? /* @__PURE__ */ jsxs11("details", { children: [
                    /* @__PURE__ */ jsx13("summary", { className: "cursor-pointer text-slate-600 hover:text-slate-900", children: "View" }),
                    /* @__PURE__ */ jsx13("pre", { className: "mt-2 text-[11px] p-2 rounded bg-slate-50 border border-slate-200 overflow-auto max-h-40", children: JSON.stringify(it.metadata, null, 2) })
                  ] }) : "\u2014" })
                ] })
              ] }) })
            ] }, it.id);
          })
        ] }),
        /* @__PURE__ */ jsx13("div", { className: "text-[11px] text-slate-400 mt-3", children: "Tip: click a row to expand. Sorting affects the list only, totals remain the same." })
      ] }) })
    ] })
  ] });
}

// src/studio/components/NewConversationHero.tsx
import clsx12 from "clsx";
import { useSessionDraft } from "@dopomogai/chat-react";

// src/studio/components/prefillComposerDraft.ts
function focusComposerTextareaAtEnd() {
  if (typeof document === "undefined") {
    return;
  }
  const textarea = document.querySelector('[data-composer-textarea="true"]') ?? document.querySelector("textarea");
  if (!textarea) {
    return;
  }
  textarea.focus();
  const value = textarea.value;
  textarea.setSelectionRange(value.length, value.length);
}
function prefillComposerDraft(setDraftText, prompt) {
  setDraftText(prompt);
  if (typeof window === "undefined") {
    focusComposerTextareaAtEnd();
    return;
  }
  window.requestAnimationFrame(() => {
    focusComposerTextareaAtEnd();
  });
}

// src/studio/components/NewConversationHero.tsx
import { jsx as jsx14, jsxs as jsxs12 } from "react/jsx-runtime";
var NewConversationHero = ({ suggestions, loading, className, title, subtitle }) => {
  const { setDraftText } = useSessionDraft();
  const onPick = (prompt) => {
    prefillComposerDraft(setDraftText, prompt);
  };
  const skeletons = Array.from({ length: 4 }).map((_, i) => /* @__PURE__ */ jsxs12("div", { className: clsx12("rounded-xl border border-slate-200 bg-white p-3", "animate-pulse"), children: [
    /* @__PURE__ */ jsx14("div", { className: "h-4 bg-slate-100 rounded w-[85%] mb-2" }),
    /* @__PURE__ */ jsx14("div", { className: "h-4 bg-slate-100 rounded w-[60%]" })
  ] }, `sk_${i}`));
  return /* @__PURE__ */ jsx14("div", { className: clsx12("w-full flex items-center justify-center px-6", className), children: /* @__PURE__ */ jsxs12("div", { className: "w-full max-w-3xl text-center select-none", children: [
    /* @__PURE__ */ jsx14("div", { className: "text-slate-900 font-semibold text-lg", children: title || "New conversation" }),
    /* @__PURE__ */ jsx14("div", { className: "mt-2 text-slate-500 text-sm", children: subtitle || "Pick a suggestion or ask your own question." }),
    /* @__PURE__ */ jsx14("div", { className: "mt-6 grid grid-cols-1 sm:grid-cols-2 gap-2 text-left", children: loading ? skeletons : suggestions.map((s) => /* @__PURE__ */ jsx14(
      "button",
      {
        type: "button",
        onClick: () => onPick(s.prompt),
        className: clsx12(
          "rounded-xl border border-slate-200 bg-white p-3 text-sm text-slate-700",
          "hover:border-slate-300 hover:bg-slate-50 transition-colors",
          "text-left"
        ),
        title: s.prompt,
        children: /* @__PURE__ */ jsxs12("div", { className: "line-clamp-3", children: [
          "\u201C",
          s.prompt,
          "\u201D"
        ] })
      },
      s.id || s.prompt
    )) })
  ] }) });
};

// src/studio/components/NextPromptSuggestions.tsx
import { useNextPromptSuggestions } from "@dopomogai/chat-react";
import { useSessionDraft as useSessionDraft2 } from "@dopomogai/chat-react";
import { jsx as jsx15, jsxs as jsxs13 } from "react/jsx-runtime";
var NextPromptSuggestions = ({
  limit = 4,
  className
}) => {
  const { items, loading, ready } = useNextPromptSuggestions(limit);
  const { setDraftText } = useSessionDraft2();
  if (!ready || loading || items.length === 0) return null;
  return /* @__PURE__ */ jsxs13("div", { className: className || "mt-3 px-3 py-2 border border-slate-200 bg-white rounded-lg shadow-sm", children: [
    /* @__PURE__ */ jsx15("div", { className: "text-[10px] font-bold uppercase text-slate-400 mb-2", children: "Try next" }),
    /* @__PURE__ */ jsx15("div", { className: "flex flex-wrap gap-2", children: items.map((s) => /* @__PURE__ */ jsx15(
      "button",
      {
        onClick: () => {
          prefillComposerDraft(setDraftText, s.prompt);
        },
        className: "text-xs px-2 py-1 rounded border border-slate-200 bg-slate-50 hover:bg-slate-100 text-slate-700",
        title: s.prompt,
        children: s.title || s.prompt
      },
      s.id || s.prompt
    )) })
  ] });
};

// src/profiles/defaults.ts
var DEFAULT_WIDGET_PROFILE_BY_SURFACE = {
  studio: "assistant_full_workspace",
  public: "visitor_public",
  simple: "visitor_public"
};
var WIDGET_PROFILE_DEFAULTS = {
  onboarding_step2: {
    id: "onboarding_step2",
    preset: "studio",
    features: {
      threadsEnabled: false,
      commentsEnabled: false,
      attachmentsEnabled: false,
      historyEnabled: false,
      tabsEnabled: false
    },
    composerCapabilities: {
      enableKB: false,
      enableUpload: false,
      enableLibrary: false,
      enableGroups: false
    },
    promptBehavior: {
      prefillOnly: true,
      autoSend: false
    },
    layout: {
      showHeader: false,
      showSidebar: false,
      showTabs: false,
      showHero: true,
      showNextPromptSuggestions: true,
      title: "Onboarding Assistant"
    },
    pageContext: {
      enabled: false,
      mode: "summary"
    },
    workspaceMode: "assistant",
    interactionMode: "test",
    flowStep: "step2",
    readinessSummary: null
  },
  assistant_panel: {
    id: "assistant_panel",
    preset: "studio",
    features: {
      threadsEnabled: false,
      commentsEnabled: false,
      attachmentsEnabled: true,
      historyEnabled: false,
      tabsEnabled: false
    },
    composerCapabilities: {
      enableKB: true,
      enableUpload: true,
      enableLibrary: false,
      enableGroups: false
    },
    promptBehavior: {
      prefillOnly: true,
      autoSend: false
    },
    layout: {
      showHeader: false,
      showSidebar: false,
      showTabs: false,
      showHero: true,
      showNextPromptSuggestions: true,
      title: "Assistant"
    },
    pageContext: {
      enabled: false,
      mode: "summary"
    },
    workspaceMode: "assistant",
    interactionMode: "test",
    flowStep: null,
    readinessSummary: null
  },
  assistant_full_workspace: {
    id: "assistant_full_workspace",
    preset: "studio",
    features: {
      threadsEnabled: false,
      commentsEnabled: false,
      attachmentsEnabled: true,
      historyEnabled: true,
      tabsEnabled: true
    },
    composerCapabilities: {
      enableKB: true,
      enableUpload: true,
      enableLibrary: false,
      enableGroups: false
    },
    promptBehavior: {
      prefillOnly: true,
      autoSend: false
    },
    layout: {
      showHeader: false,
      showSidebar: true,
      showTabs: true,
      showHero: true,
      showNextPromptSuggestions: true,
      title: "Assistant Workspace"
    },
    pageContext: {
      enabled: false,
      mode: "summary"
    },
    workspaceMode: "assistant",
    interactionMode: "test",
    flowStep: null,
    readinessSummary: null
  },
  visitor_public: {
    id: "visitor_public",
    preset: "simple",
    features: {
      threadsEnabled: false,
      commentsEnabled: false,
      attachmentsEnabled: false,
      historyEnabled: false,
      tabsEnabled: false
    },
    composerCapabilities: {
      enableKB: false,
      enableUpload: false,
      enableLibrary: false,
      enableGroups: false
    },
    promptBehavior: {
      prefillOnly: true,
      autoSend: false
    },
    layout: {
      showHeader: false,
      showSidebar: false,
      showTabs: false,
      showHero: true,
      showNextPromptSuggestions: false,
      title: "Chat"
    },
    pageContext: {
      enabled: false,
      mode: "full"
    },
    workspaceMode: null,
    interactionMode: null,
    flowStep: null,
    readinessSummary: null
  }
};

// src/profiles/resolveProfile.ts
var getDefaultWidgetProfileId = (surface) => DEFAULT_WIDGET_PROFILE_BY_SURFACE[surface];
var resolveWidgetProfile = (input) => {
  const profileId = input.profile || getDefaultWidgetProfileId(input.surface);
  const defaults = WIDGET_PROFILE_DEFAULTS[profileId] || WIDGET_PROFILE_DEFAULTS[getDefaultWidgetProfileId(input.surface)];
  const resolvedFeatures = {
    ...defaults.features,
    ...input.features || {}
  };
  if (typeof input.enableAttachments === "boolean") {
    resolvedFeatures.attachmentsEnabled = input.enableAttachments;
  }
  const composerCapabilities = {
    ...defaults.composerCapabilities
  };
  if (typeof input.enableAttachments === "boolean") {
    composerCapabilities.enableUpload = input.enableAttachments;
    if (!input.enableAttachments) {
      composerCapabilities.enableKB = false;
    }
  }
  const layout = {
    ...defaults.layout
  };
  if (typeof input.showHeader === "boolean") {
    layout.showHeader = input.showHeader;
  }
  if (typeof input.title === "string") {
    layout.title = input.title;
  }
  return {
    ...defaults,
    id: profileId,
    surface: input.surface,
    preset: input.preset || defaults.preset,
    features: resolvedFeatures,
    composerCapabilities,
    layout,
    workspaceMode: input.workspaceMode ?? defaults.workspaceMode ?? null,
    interactionMode: input.interactionMode ?? defaults.interactionMode ?? null,
    flowStep: input.flowStep ?? defaults.flowStep ?? null,
    readinessSummary: input.readinessSummary ?? defaults.readinessSummary ?? null
  };
};

// src/studio/StudioWidget.tsx
import { useSessionActivation as useActivation } from "@dopomogai/chat-react";
import { useHeroSuggestions } from "@dopomogai/chat-react";

// src/studio/tooling/StudioToolResultRenderers.tsx
import { useMemo as useMemo7 } from "react";
import { ArtifactBlock as ArtifactBlockRenderer } from "@dopomogai/chat-ui";
import { jsx as jsx16 } from "react/jsx-runtime";
function asFileLikeArray(result) {
  if (!result) return null;
  const raw = Array.isArray(result) ? result : Array.isArray(result.files) ? result.files : result;
  if (Array.isArray(raw)) {
    const items = raw.filter(Boolean).filter((x) => typeof x === "object").filter((x) => typeof x.path === "string" && typeof x.content === "string").map((x) => ({ path: x.path, language: x.language || x.lang || "text", content: x.content }));
    return items.length ? items : null;
  }
  if (raw && typeof raw === "object" && typeof raw.path === "string" && typeof raw.content === "string") {
    return [{ path: raw.path, language: raw.language || raw.lang || "text", content: raw.content }];
  }
  return null;
}
var ArtifactListRenderer = ({ result }) => {
  const files = useMemo7(() => asFileLikeArray(result), [result]);
  if (!files) return null;
  return /* @__PURE__ */ jsx16("div", { className: "flex flex-col gap-4", children: files.map((f, idx) => /* @__PURE__ */ jsx16(
    ArtifactBlockRenderer,
    {
      block: {
        id: `tool_artifact_${idx}_${f.path}`,
        kind: "artifact",
        messageId: "tool-result",
        role: "assistant",
        path: f.path,
        language: f.language || "text",
        content: f.content,
        raw: `### '${f.path}'
\`\`\`\`${f.language || "text"}
${f.content}
\`\`\`\``
      },
      surface: "studio"
    }
  )) });
};
var StudioToolResultRenderers = {
  // Explicit tool names you expect from backend can go here:
  // artifacts: ArtifactListRenderer,
  // repo_write: ArtifactListRenderer,
  // But we also provide a generic renderer key you can map to:
  // If backend emits tool_result.name === 'files' or 'artifacts', it will use this.
  files: ArtifactListRenderer,
  artifacts: ArtifactListRenderer
};

// src/studio/StudioWidget.tsx
import { Fragment as Fragment3, jsx as jsx17, jsxs as jsxs14 } from "react/jsx-runtime";
function getUrlParam(key) {
  if (typeof window === "undefined") return null;
  return new URL(window.location.href).searchParams.get(key);
}
function setUrlParam(key, value) {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  if (value) url.searchParams.set(key, value);
  else url.searchParams.delete(key);
  window.history.replaceState({}, "", url.toString());
}
var StudioWidgetContent = ({ agents, onAgentChange, layout, composerCapabilities }) => {
  const client = useChatClient2();
  const { isLoading, messages } = useChat();
  const { activate, createAndActivate } = useActivation({ onActivate: (id) => setUrlParam("cid", id) });
  const {
    activeConversationId,
    conversations,
    config,
    isSidebarCollapsed,
    openTabs,
    tabGroups,
    ungroupedTabIds
  } = useStore2(
    client.store,
    useShallow2((s) => ({
      activeConversationId: s.activeConversationId,
      conversations: s.conversations,
      config: s.config,
      isSidebarCollapsed: s.isSidebarCollapsed,
      openTabs: s.openTabs,
      tabGroups: s.tabGroups || [],
      ungroupedTabIds: s.ungroupedTabIds || []
    }))
  );
  const {
    sessionOverrides,
    sessionContextUsage,
    sessionDraftAttachmentsSignature,
    sessionMessageCount,
    sessionLastMessageId,
    sessionLastRunCostUsd
  } = useStore2(
    client.store,
    useShallow2((s) => {
      const cid = s.activeConversationId;
      const sess = cid ? s.sessions[cid] : null;
      const attachmentsSig = (sess?.draftAttachments || []).map((a) => `${a.fileId || a.clientId}:${a.status}`).join("|");
      const msgCount = sess?.messages?.length || 0;
      const lastMsgId = msgCount > 0 ? sess?.messages?.[msgCount - 1]?.id || null : null;
      return {
        sessionOverrides: sess?.runOverrides || {},
        sessionContextUsage: sess?.contextUsage || void 0,
        sessionDraftAttachmentsSignature: attachmentsSig,
        sessionMessageCount: msgCount,
        sessionLastMessageId: lastMsgId,
        sessionLastRunCostUsd: sess?.lastRunCost?.totalCostUsd ?? null
      };
    })
  );
  const isEmptyConversation = (messages?.length || 0) === 0;
  const [isSidebarOpenMobile, setIsSidebarOpenMobile] = useState8(false);
  const [availableModels, setAvailableModels] = useState8([]);
  const [isContextModalOpen, setIsContextModalOpen] = useState8(false);
  const [isCostModalOpen, setIsCostModalOpen] = useState8(false);
  const [costLoading, setCostLoading] = useState8(false);
  const [costError, setCostError] = useState8(null);
  const [costData, setCostData] = useState8(null);
  const [agentDefaultDescriptor, setAgentDefaultDescriptor] = useState8("");
  const bootstrappedRef = useRef2(false);
  const initialSelectionDoneRef = useRef2(false);
  const usageAbortRef = useRef2(null);
  useEffect2(() => {
    const st = client.store.getState();
    if ((!st.tabGroups || st.tabGroups.length === 0) && (!st.ungroupedTabIds || st.ungroupedTabIds.length === 0)) {
      if ((st.openTabs || []).length > 0 && st.migrateOpenTabsToGroups) {
        st.migrateOpenTabsToGroups();
      }
    }
  }, [client]);
  useEffect2(() => {
    if (bootstrappedRef.current) return;
    bootstrappedRef.current = true;
    void client.conversationManager.listConversations();
    modelService.getAvailableModels().then((res) => {
      const list = Array.isArray(res) ? res : res?.models || [];
      const mapped = list.filter((m) => m && m.id).map((m) => ({
        id: m.id,
        name: m.name || m.label || m.id,
        provider: m.provider || "unknown",
        context_window_tokens: m.context_window_tokens ?? null
      }));
      setAvailableModels(mapped);
    }).catch((e) => console.error("[StudioWidget] getAvailableModels failed", e));
  }, [client]);
  useEffect2(() => {
    const agentId = config.agentId;
    if (!agentId) return;
    let cancelled = false;
    (async () => {
      const res = await agentService.getAgent(agentId);
      if (cancelled) return;
      if (!isApiError(res)) setAgentDefaultDescriptor(res.llm_model || "");
    })().catch((e) => console.warn("[StudioWidget] getAgent failed", e));
    return () => {
      cancelled = true;
    };
  }, [config.agentId]);
  useEffect2(() => {
    if (initialSelectionDoneRef.current) return;
    const cidParam = getUrlParam("cid");
    const st = client.store.getState();
    const pickInitialId = () => {
      if (cidParam) return cidParam;
      if (st.activeConversationId && st.sessions?.[st.activeConversationId]) return st.activeConversationId;
      if (conversations.length > 0) {
        const sorted = [...conversations].sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
        return sorted[0]?.id || null;
      }
      return null;
    };
    const initialId = pickInitialId();
    initialSelectionDoneRef.current = true;
    if (initialId) activate(initialId, { load: true });
    else createAndActivate({ title: "New Chat" });
  }, [client, activate, createAndActivate, conversations.length]);
  useEffect2(() => {
    if (!activeConversationId) return;
    setUrlParam("cid", activeConversationId);
    const st = client.store.getState();
    const exists = st.openTabs?.some((t) => t.id === activeConversationId);
    if (!exists) {
      const meta = st.conversations.find((c) => c.id === activeConversationId);
      const title = meta?.title || meta?.first_user_message_preview || "New Chat";
      st.setOpenTabs([...st.openTabs || [], { id: activeConversationId, title }]);
      const anyStore = st;
      if (anyStore.ungroupedTabIds && Array.isArray(anyStore.ungroupedTabIds)) {
        if (!anyStore.ungroupedTabIds.includes(activeConversationId)) {
          anyStore.ungroupedTabIds.push(activeConversationId);
        }
      }
    }
  }, [activeConversationId, client]);
  const handleNewChat = useCallback2(() => {
    const st = client.store.getState();
    const prevActiveId = st.activeConversationId || null;
    const prevOpenTabs = st.openTabs || [];
    const newId = createAndActivate({ title: "New Chat" });
    const nowTabs = client.store.getState().openTabs || [];
    const newIdx = nowTabs.findIndex((t) => t.id === newId);
    const prevIdx = prevActiveId ? prevOpenTabs.findIndex((t) => t.id === prevActiveId) : -1;
    if (newIdx > -1 && prevIdx > -1) {
      const copy = [...nowTabs];
      const [spliced] = copy.splice(newIdx, 1);
      copy.splice(prevIdx + 1, 0, spliced);
      client.store.getState().setOpenTabs(copy);
    }
    const s = client.store.getState();
    if ((s.tabGroups?.length || 0) > 0 || (s.ungroupedTabIds?.length || 0) > 0) {
      const findContainerOf = (tid) => {
        if (s.ungroupedTabIds?.includes(tid)) return { id: null, items: s.ungroupedTabIds };
        for (const g of s.tabGroups || []) {
          if ((g.tabIds || []).includes(tid)) return { id: g.id, items: g.tabIds };
        }
        return null;
      };
      if (prevActiveId) {
        const src = findContainerOf(prevActiveId);
        if (src) {
          const idx = src.items.indexOf(prevActiveId);
          s.moveTab?.(newId, src.id, Math.max(0, idx + 1));
        } else {
          s.moveTab?.(newId, null, (s.ungroupedTabIds || []).length);
        }
      } else {
        s.moveTab?.(newId, null, (s.ungroupedTabIds || []).length);
      }
    }
    if (window.innerWidth < 768) setIsSidebarOpenMobile(false);
  }, [client, createAndActivate]);
  const handleSelectConversation = useCallback2(
    (id) => {
      if (!id) return handleNewChat();
      activate(id, { load: true });
      if (window.innerWidth < 768) setIsSidebarOpenMobile(false);
    },
    [activate, handleNewChat]
  );
  const handleCloseTab = useCallback2(
    (id) => {
      const st = client.store.getState();
      const anySt = st;
      if (anySt.closeTab) {
        anySt.closeTab(id);
      } else {
        st.setOpenTabs((st.openTabs || []).filter((t) => t.id !== id));
      }
      if (st.activeConversationId === id) {
        const remaining = anySt.openTabs || st.openTabs || [];
        const currentIndex = remaining.findIndex((t) => t.id === id);
        const next = remaining[currentIndex + 1]?.id || remaining[currentIndex - 1]?.id || remaining[remaining.length - 1]?.id;
        if (next) activate(next, { load: true });
        else handleNewChat();
      }
    },
    [client, activate, handleNewChat]
  );
  const handleReorderTabs = useCallback2((oldIndex, newIndex) => {
    const st = client.store.getState();
    const arr = [...st.openTabs || []];
    if (oldIndex < 0 || newIndex < 0 || oldIndex >= arr.length || newIndex >= arr.length) return;
    const [moved] = arr.splice(oldIndex, 1);
    arr.splice(newIndex, 0, moved);
    st.setOpenTabs(arr);
    const anySt = st;
    if ((anySt.tabGroups?.length || 0) === 0 && Array.isArray(anySt.ungroupedTabIds)) {
      anySt.ungroupedTabIds = arr.map((t) => t.id);
    }
  }, [client]);
  const handleMoveTabGrouped = useCallback2((tabId, containerId, targetIndex) => {
    const st = client.store.getState();
    if (st.moveTab) st.moveTab(tabId, containerId, targetIndex);
  }, [client]);
  const handleCreateGroup = useCallback2(() => {
    const st = client.store.getState();
    st.createTabGroup?.("Group", "blue");
  }, [client]);
  const handleRenameGroup = useCallback2((groupId, name) => {
    const st = client.store.getState();
    st.renameTabGroup?.(groupId, name);
  }, [client]);
  const handleChangeGroupColor = useCallback2((groupId, color) => {
    const st = client.store.getState();
    st.setTabGroupColor?.(groupId, color);
  }, [client]);
  const handleToggleGroupCollapsed = useCallback2((groupId, collapsed) => {
    const st = client.store.getState();
    st.setTabGroupCollapsed?.(groupId, collapsed);
  }, [client]);
  const handleDeleteGroup = useCallback2((groupId) => {
    const st = client.store.getState();
    st.deleteTabGroup?.(groupId);
  }, [client]);
  const activeModelOverride = sessionOverrides.model_descriptor || "";
  const effectiveModelDescriptor = activeModelOverride || agentDefaultDescriptor || "";
  const effectiveModel = useMemo8(() => {
    const found = availableModels.find((m) => m.id === effectiveModelDescriptor);
    if (found) return found;
    if (effectiveModelDescriptor) return { id: effectiveModelDescriptor, name: effectiveModelDescriptor, provider: "unknown" };
    return null;
  }, [availableModels, effectiveModelDescriptor]);
  const modelSettings = sessionOverrides.model_settings || {};
  const setModelOverride = useCallback2(
    (modelId) => {
      if (!activeConversationId) return;
      client.store.getState().setSessionRunOverrides(activeConversationId, { model_descriptor: modelId || void 0 });
    },
    [activeConversationId, client]
  );
  const setModelSettingsOverride = useCallback2(
    (settings) => {
      if (!activeConversationId) return;
      client.store.getState().setSessionRunOverrides(activeConversationId, { model_settings: settings || {} });
    },
    [activeConversationId, client]
  );
  const computeSignature = useCallback2((opts) => {
    const { cid, model, draftText, uploadedFileIds, msgCount, lastMsgId } = opts;
    return [cid, model, msgCount, lastMsgId || "", draftText.trim(), uploadedFileIds.join(",")].join("|");
  }, []);
  const recomputeContextUsage = useCallback2(async (reason) => {
    const cid = activeConversationId;
    const agentId = config.agentId;
    if (!cid || !agentId) return;
    const sess = client.store.getState().sessions?.[cid];
    if (!sess) return;
    const hasPendingUploads = (sess.draftAttachments || []).some(
      (a) => a.status === "queued" || a.status === "uploading"
    );
    if (hasPendingUploads) {
      return;
    }
    const uploadedFileIds = (sess.draftAttachments || []).filter((a) => a.status === "uploaded" && !!a.fileId).map((a) => a.fileId);
    const msgCount = sess.messages?.length || 0;
    const lastMsgId = msgCount ? sess.messages[msgCount - 1]?.id : void 0;
    const signature = computeSignature({
      cid,
      model: effectiveModelDescriptor || "",
      draftText: sess.draftText || "",
      uploadedFileIds,
      msgCount,
      lastMsgId
    });
    const prev = sess.contextUsage;
    const now = Date.now();
    if (prev && prev.signature === signature && now - prev.updatedAt < 15e3) return;
    if (usageAbortRef.current) {
      usageAbortRef.current.abort();
      usageAbortRef.current = null;
    }
    const controller = new AbortController();
    usageAbortRef.current = controller;
    try {
      const parts = [];
      if (sess.draftText && sess.draftText.trim()) parts.push({ type: "text", content: sess.draftText });
      for (const fid of uploadedFileIds) parts.push({ type: "file", file_id: fid, media_type: "application/octet-stream" });
      if (parts.length === 0) return;
      const resp = await contextAnalysisService.getUsageFromParts(
        {
          agent_id: agentId,
          conversation_id: cid,
          parts,
          model_descriptor_override: effectiveModelDescriptor || void 0
        },
        { signal: controller.signal }
      );
      if (resp?.isError) return;
      const usage = {
        ...resp,
        updatedAt: now,
        model_descriptor: effectiveModelDescriptor || "",
        signature
      };
      client.store.getState().setContextUsage(cid, usage);
    } catch (e) {
      if (e?.name === "CanceledError" || e?.name === "AbortError") {
      } else {
        console.warn("[StudioWidget] context usage failed", reason, e);
      }
    } finally {
      if (usageAbortRef.current === controller) {
        usageAbortRef.current = null;
      }
    }
  }, [activeConversationId, config.agentId, computeSignature, effectiveModelDescriptor, client]);
  const recomputeDebounced = useMemo8(() => {
    let t;
    return () => {
      clearTimeout(t);
      t = setTimeout(() => void recomputeContextUsage("debounced"), 700);
    };
  }, [recomputeContextUsage]);
  useEffect2(() => {
    void recomputeContextUsage("conversation-change");
  }, [activeConversationId, effectiveModelDescriptor, recomputeContextUsage]);
  useEffect2(() => {
    if (!activeConversationId) return;
    recomputeDebounced();
  }, [
    activeConversationId,
    sessionDraftAttachmentsSignature,
    sessionMessageCount,
    sessionLastMessageId,
    effectiveModelDescriptor,
    recomputeDebounced
  ]);
  useEffect2(() => {
    if (!activeConversationId) return;
    let idleTimer = null;
    let lastDraft = (client.store.getState().sessions?.[activeConversationId]?.draftText || "").trim();
    const unsubscribe = client.store.subscribe((state) => {
      const nextDraft = (state.sessions?.[activeConversationId]?.draftText || "").trim();
      if (nextDraft === lastDraft) return;
      lastDraft = nextDraft;
      if (idleTimer) clearTimeout(idleTimer);
      idleTimer = setTimeout(() => {
        void recomputeContextUsage("draft-idle");
      }, 1200);
    });
    return () => {
      unsubscribe();
      if (idleTimer) clearTimeout(idleTimer);
    };
  }, [activeConversationId, client, recomputeContextUsage]);
  const headerContextUsage = useMemo8(() => {
    const u = sessionContextUsage;
    if (!u) return null;
    return {
      total_tokens: u.total_tokens,
      model_context_limit: u.model_context_limit,
      is_limit_exceeded: u.is_limit_exceeded,
      file_tokens_est: u.file_tokens_est
    };
  }, [sessionContextUsage]);
  const openCostModal = useCallback2(async () => {
    if (!activeConversationId) return;
    setIsCostModalOpen(true);
    if (costData?.conversation_id === activeConversationId) return;
    setCostLoading(true);
    setCostError(null);
    try {
      const res = await conversationUsageService.getConversationUsageLogs(activeConversationId);
      if (isApiError(res)) {
        setCostError(res.message || "Failed to load usage logs");
        setCostData(null);
      } else {
        setCostData(res);
      }
    } catch (e) {
      setCostError(e?.message || "Failed to load usage logs");
      setCostData(null);
    } finally {
      setCostLoading(false);
    }
  }, [activeConversationId, costData?.conversation_id]);
  const lastRunCostUsd = activeConversationId ? sessionLastRunCostUsd ?? null : null;
  const costUsd = costData?.conversation_id === activeConversationId ? costData?.total_cost_usd ?? 0 : lastRunCostUsd ?? null;
  const { items: heroSuggestions, loading: heroLoading } = useHeroSuggestions(config.agentId, isEmptyConversation, 4);
  const tabMetaMap = useMemo8(() => {
    const map = {};
    (openTabs || []).forEach((t) => {
      map[t.id] = { title: t.title || "Untitled" };
    });
    return map;
  }, [openTabs]);
  return /* @__PURE__ */ jsxs14(Fragment3, { children: [
    /* @__PURE__ */ jsx17(
      StudioLayout,
      {
        sidebar: layout.showSidebar ? /* @__PURE__ */ jsx17(
          StudioSidebar,
          {
            conversations,
            activeConversationId,
            onSelectConversation: handleSelectConversation,
            onDeleteConversation: (id) => client.conversationManager.deleteConversation(id),
            onRenameConversation: (id, t) => client.conversationManager.renameConversation(id, t),
            isCollapsed: isSidebarCollapsed,
            onToggleCollapse: () => client.store.getState().setSidebarCollapsed(!isSidebarCollapsed)
          }
        ) : null,
        header: /* @__PURE__ */ jsx17(
          StudioHeader,
          {
            agents,
            activeAgentId: config.agentId || null,
            onAgentChange,
            models: availableModels,
            activeModelId: sessionOverrides.model_descriptor || "",
            onModelChange: setModelOverride,
            effectiveModel,
            modelSettings,
            onModelSettingsChange: setModelSettingsOverride,
            onOpenAgentPanel: () => alert("Agent panel coming soon"),
            contextUsage: headerContextUsage,
            onOpenContextDetails: () => setIsContextModalOpen(true),
            costUsd: costUsd || 0,
            isCostLoading: costLoading,
            onOpenCostDetails: openCostModal,
            isSettingsOpen: false,
            onToggleSettings: () => {
            }
          }
        ),
        tabs: layout.showTabs ? /* @__PURE__ */ jsx17(
          StudioTabs,
          {
            tabs: openTabs,
            onReorderTabs: handleReorderTabs,
            tabGroups,
            ungroupedTabIds,
            tabMeta: tabMetaMap,
            onMoveTab: handleMoveTabGrouped,
            onCreateGroup: handleCreateGroup,
            onRenameGroup: handleRenameGroup,
            onChangeGroupColor: handleChangeGroupColor,
            onToggleGroupCollapsed: handleToggleGroupCollapsed,
            onDeleteGroup: handleDeleteGroup,
            activeTabId: activeConversationId,
            onSelectTab: handleSelectConversation,
            onCloseTab: handleCloseTab,
            onNewChat: handleNewChat
          }
        ) : null,
        composerPlacement: isEmptyConversation ? "center" : "bottom",
        hero: isEmptyConversation && layout.showHero ? /* @__PURE__ */ jsx17(NewConversationHero, { suggestions: heroSuggestions, loading: heroLoading }) : void 0,
        messages: /* @__PURE__ */ jsxs14("div", { className: "h-full pb-48", children: [
          /* @__PURE__ */ jsx17(MessageList2, { className: "h-full pb-2" }, activeConversationId || "none"),
          layout.showNextPromptSuggestions ? /* @__PURE__ */ jsx17(NextPromptSuggestions, { className: "w-full max-w-3xl mx-auto mt-2" }) : null
        ] }),
        composer: GlassTheme.Composer ? /* @__PURE__ */ jsx17(
          GlassTheme.Composer,
          {
            onSend: (text) => client.sendMessage(activeConversationId, text),
            isLoading,
            capabilities: composerCapabilities
          }
        ) : null,
        isSidebarOpenMobile,
        onSetSidebarOpenMobile: setIsSidebarOpenMobile,
        isSidebarCollapsed
      }
    ),
    /* @__PURE__ */ jsx17(
      ContextUsageModal,
      {
        open: isContextModalOpen,
        onClose: () => setIsContextModalOpen(false),
        usage: sessionContextUsage || null
      }
    ),
    /* @__PURE__ */ jsx17(
      ConversationCostModal,
      {
        open: isCostModalOpen,
        onClose: () => setIsCostModalOpen(false),
        data: costData?.conversation_id === activeConversationId ? costData : null,
        isLoading: costLoading,
        error: costError
      }
    )
  ] });
};
var StudioWidget = ({ config, className }) => {
  const [currentAgentId, setCurrentAgentId] = useState8(config.agentId || null);
  const [agentList, setAgentList] = useState8([]);
  const [isInitializing, setIsInitializing] = useState8(true);
  const resolvedProfile = useMemo8(
    () => resolveWidgetProfile({
      surface: "studio",
      profile: config.profile,
      preset: config.preset,
      enableAttachments: config.features?.attachmentsEnabled,
      features: config.features,
      workspaceMode: config.workspaceMode,
      interactionMode: config.interactionMode,
      flowStep: config.flowStep,
      readinessSummary: config.readinessSummary
    }),
    [
      config.profile,
      config.preset,
      config.features,
      config.workspaceMode,
      config.interactionMode,
      config.flowStep,
      config.readinessSummary
    ]
  );
  const resolvedFeatures = resolvedProfile.features;
  const engineParam = getUrlParam("engine");
  const useV2Engine = engineParam?.toLowerCase() === "v2";
  useEffect2(() => {
    setCurrentAgentId(config.agentId || null);
  }, [config.agentId]);
  useEffect2(() => {
    let cancelled = false;
    const init = async () => {
      setIsInitializing(true);
      try {
        if (config.authMode === "PLATFORM" && config.authToken) {
          const res = await fetch(`${config.apiBaseUrl}/api/v1/agents`, {
            headers: { Authorization: `Bearer ${config.authToken}` }
          });
          const data = await res.json();
          if (cancelled) return;
          if (Array.isArray(data)) {
            const sorted = data.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
            const options = sorted.map((a) => ({ id: a.id, name: a.name, model: a.llm_model }));
            setAgentList(options);
            if (!currentAgentId && options.length > 0) {
              setCurrentAgentId(options[0].id);
            }
          }
        } else {
          setAgentList([]);
        }
      } catch (e) {
        console.error("[StudioWidget] init failed", e);
      } finally {
        if (!cancelled) setIsInitializing(false);
      }
    };
    void init();
    return () => {
      cancelled = true;
    };
  }, [config.authToken, config.apiBaseUrl, config.authMode]);
  if (isInitializing && !currentAgentId) {
    return /* @__PURE__ */ jsx17("div", { className: "h-full flex items-center justify-center text-slate-400 animate-pulse", children: "Loading Studio..." });
  }
  if (!currentAgentId) {
    return /* @__PURE__ */ jsx17("div", { className: "h-full flex items-center justify-center text-slate-400", children: "No Agent Selected" });
  }
  const providerProps = {
    preset: resolvedProfile.preset,
    features: resolvedFeatures,
    interactionMode: resolvedProfile.interactionMode || void 0,
    workspaceMode: resolvedProfile.workspaceMode || void 0,
    flowStep: resolvedProfile.flowStep ?? void 0,
    readinessSummary: resolvedProfile.readinessSummary ?? void 0,
    persistNamespace: config.persistNamespace,
    assistantProfileId: config.assistantProfileId ?? void 0,
    runtimeAgentId: config.runtimeAgentId ?? void 0,
    subjectAgentId: config.subjectAgentId ?? void 0,
    subjectContextKey: config.subjectContextKey ?? void 0,
    onboardingSessionId: config.onboardingSessionId ?? void 0,
    scanId: config.scanId ?? void 0,
    allowClientTools: config.allowClientTools,
    requestContext: config.requestContext,
    turnPromptOverride: config.turnPromptOverride
  };
  return /* @__PURE__ */ jsx17("div", { className: clsx13("h-full w-full", className), "data-widget-profile": resolvedProfile.id, children: /* @__PURE__ */ jsx17(
    ChatProvider3,
    {
      agentId: currentAgentId,
      apiBaseUrl: config.apiBaseUrl,
      authMode: config.authMode || "WIDGET",
      authToken: config.authToken,
      tools: config.tools,
      ...providerProps,
      toolResultRenderers: StudioToolResultRenderers,
      useV2Engine,
      children: /* @__PURE__ */ jsx17(ChatUIProvider2, { theme: GlassTheme, children: /* @__PURE__ */ jsx17(
        StudioWidgetContent,
        {
          agents: agentList,
          onAgentChange: setCurrentAgentId,
          layout: resolvedProfile.layout,
          composerCapabilities: resolvedProfile.composerCapabilities
        }
      ) })
    },
    currentAgentId
  ) });
};

// src/public/PublicWidget.tsx
import { useMemo as useMemo9 } from "react";
import clsx14 from "clsx";
import { ChatProvider as ChatProvider4, useChatClient as useChatClient3, useChat as useChat2 } from "@dopomogai/chat-react";
import { ChatUIProvider as ChatUIProvider3, MessageList as MessageList3, GlassTheme as GlassTheme2 } from "@dopomogai/chat-ui";
import { jsx as jsx18, jsxs as jsxs15 } from "react/jsx-runtime";
var PublicWidgetShell = ({ showHeader, title, capabilities }) => {
  const client = useChatClient3();
  const { isLoading, activeId, status } = useChat2();
  const Theme = GlassTheme2;
  return /* @__PURE__ */ jsxs15("div", { className: "flex flex-col h-full w-full overflow-hidden bg-slate-50 text-slate-900", children: [
    showHeader && /* @__PURE__ */ jsxs15("div", { className: "shrink-0 h-12 px-4 border-b border-slate-200 bg-white/80 backdrop-blur flex items-center justify-between", children: [
      /* @__PURE__ */ jsx18("div", { className: "text-sm font-semibold truncate", children: title || "Chat" }),
      /* @__PURE__ */ jsx18("div", { className: "text-[11px] text-slate-500", children: status === "CONNECTED" || status === "CONNECTING" ? "Online" : status === "ERROR" ? "Error" : "Ready" })
    ] }),
    /* @__PURE__ */ jsx18("div", { className: "flex-1 min-h-0", children: /* @__PURE__ */ jsx18(MessageList3, { className: "h-full" }) }),
    /* @__PURE__ */ jsx18("div", { className: "shrink-0 p-3 border-t border-slate-200 bg-white/80 backdrop-blur", children: Theme.Composer ? /* @__PURE__ */ jsx18(
      Theme.Composer,
      {
        onSend: (text) => client.sendMessage(activeId, text),
        isLoading,
        disabled: false,
        capabilities
      }
    ) : null })
  ] });
};
var PublicWidget = ({
  apiBaseUrl,
  widgetVersionId,
  installationId,
  templateId,
  agentId,
  visitorId,
  allowClientTools = false,
  authToken,
  enableAttachments,
  features,
  preset,
  profile,
  workspaceMode,
  interactionMode,
  flowStep,
  readinessSummary,
  persistNamespace,
  className,
  showHeader,
  title
}) => {
  const theme = useMemo9(() => GlassTheme2, []);
  const resolvedProfile = useMemo9(
    () => resolveWidgetProfile({
      surface: "public",
      profile,
      preset,
      title,
      showHeader,
      enableAttachments,
      features,
      workspaceMode,
      interactionMode,
      flowStep,
      readinessSummary
    }),
    [
      profile,
      preset,
      title,
      showHeader,
      enableAttachments,
      features,
      workspaceMode,
      interactionMode,
      flowStep,
      readinessSummary
    ]
  );
  const providerProps = {
    authMode: "VISITOR_WIDGET",
    widgetVersionId,
    installationId,
    templateId,
    visitorId,
    authToken,
    agentId,
    apiBaseUrl,
    allowClientTools,
    preset: resolvedProfile.preset,
    features: resolvedProfile.features,
    interactionMode: resolvedProfile.interactionMode || void 0,
    workspaceMode: resolvedProfile.workspaceMode || void 0,
    flowStep: resolvedProfile.flowStep ?? void 0,
    readinessSummary: resolvedProfile.readinessSummary ?? void 0,
    persistNamespace
  };
  return /* @__PURE__ */ jsx18("div", { className: clsx14("h-full w-full", className), "data-widget-profile": resolvedProfile.id, children: /* @__PURE__ */ jsx18(ChatProvider4, { ...providerProps, children: /* @__PURE__ */ jsx18(ChatUIProvider3, { theme, children: /* @__PURE__ */ jsx18(
    PublicWidgetShell,
    {
      showHeader: resolvedProfile.layout.showHeader,
      title: resolvedProfile.layout.title,
      capabilities: resolvedProfile.composerCapabilities
    }
  ) }) }) });
};

// src/simple/SimpleWidget.tsx
import { useMemo as useMemo10 } from "react";
import { jsx as jsx19 } from "react/jsx-runtime";
var SimpleWidget = ({
  enableAttachments,
  features,
  profile,
  workspaceMode,
  interactionMode,
  flowStep,
  readinessSummary,
  showHeader,
  title,
  ...rest
}) => {
  const resolvedProfile = useMemo10(
    () => resolveWidgetProfile({
      surface: "simple",
      profile,
      preset: "simple",
      title,
      showHeader,
      enableAttachments,
      features,
      workspaceMode,
      interactionMode,
      flowStep,
      readinessSummary
    }),
    [
      profile,
      title,
      showHeader,
      enableAttachments,
      features,
      workspaceMode,
      interactionMode,
      flowStep,
      readinessSummary
    ]
  );
  return /* @__PURE__ */ jsx19(
    PublicWidget,
    {
      ...rest,
      profile: resolvedProfile.id,
      workspaceMode: resolvedProfile.workspaceMode || void 0,
      interactionMode: resolvedProfile.interactionMode || void 0,
      flowStep: resolvedProfile.flowStep ?? void 0,
      readinessSummary: resolvedProfile.readinessSummary ?? void 0,
      preset: resolvedProfile.preset,
      showHeader: resolvedProfile.layout.showHeader,
      title: resolvedProfile.layout.title,
      enableAttachments: resolvedProfile.features.attachmentsEnabled !== false,
      features: resolvedProfile.features
    }
  );
};

// src/WidgetPresetsApp.tsx
import { jsx as jsx20, jsxs as jsxs16 } from "react/jsx-runtime";
var WidgetPresetsApp = ({ mode = "demo" }) => {
  return /* @__PURE__ */ jsx20("div", { className: "flex h-full w-full items-center justify-center rounded-xl border border-slate-200 bg-slate-50 p-6 text-slate-700", children: /* @__PURE__ */ jsxs16("div", { className: "max-w-xl space-y-2 text-center", children: [
    /* @__PURE__ */ jsx20("h3", { className: "text-lg font-semibold text-slate-900", children: "Widget Presets Demo" }),
    /* @__PURE__ */ jsxs16("p", { className: "text-sm", children: [
      "Phase-1 rollout is scoped to the ",
      /* @__PURE__ */ jsx20("code", { children: "standard" }),
      " preset. Additional presets remain experimental."
    ] }),
    /* @__PURE__ */ jsxs16("p", { className: "text-xs text-slate-500", children: [
      "Mode: ",
      mode
    ] })
  ] }) });
};
export {
  AudioWidget,
  DEFAULT_WIDGET_PROFILE_BY_SURFACE,
  PublicWidget,
  SimpleWidget,
  StandardWidget,
  StudioWidget,
  WIDGET_PROFILE_DEFAULTS,
  WidgetPresetsApp,
  getDefaultWidgetProfileId,
  resolveWidgetProfile
};
